import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Plus,
  ArrowLeft,
  PackageOpen,
  Scissors,
  CheckCircle,
  UserPlus,
  X,
  Search,
  Copy,
  ArrowRightCircle,
  Trash2,
  Edit,
  ClipboardList,
  Grid,
  DollarSign,
  Truck,
  UserSquare,
  FolderPlus,
  FileSpreadsheet,
  FileText,
  FileJson,
  MessageCircle,
  Smile,
  Printer,
  Ticket,
  AlertCircle,
  Bell,
} from "lucide-react";
import { OrderDetailsView } from "./OrderDetailsView";
import { handlePrepaidDeduction, refundPrepaidDeduction } from "../utils/prepaidUtils";
import { CustomerHistoryModal } from "./CustomerHistoryModal";
import { StringerAllocationModal } from "./StringerAllocationModal";
import { AnalyticsView } from "./AnalyticsView";
import { OrdersView } from "./OrdersView";
import { SettingsView } from "./SettingsView";
import { applyPhoneMask, applyCpfCnpjMask } from "../utils/masks";
import { FeedbackModal } from "./FeedbackModal";
import { useAuth } from "../contexts/AuthContext";
import { LegalDocsModal } from "./LegalDocsModal";
import { validateCpfCnpj } from "../utils/validators";

const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
  const cep = e.target.value.replace(/\D/g, "");
  if (cep.length === 8) {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      const form = e.target.closest("form");
      if (!data.erro && form) {
        const addr = form.querySelector('[name="address"]') as HTMLInputElement;
        const neigh = form.querySelector(
          '[name="neighborhood"]',
        ) as HTMLInputElement;
        const city = form.querySelector('[name="city"]') as HTMLInputElement;
        const state = form.querySelector('[name="state"]') as HTMLInputElement;

        if (addr && data.logradouro) addr.value = data.logradouro;
        if (neigh && data.bairro) neigh.value = data.bairro;
        if (city && data.localidade) city.value = data.localidade;
        if (state && data.uf) state.value = data.uf;
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    }
  }
};

const getFieldConfig = (
  appSettings: any,
  type: "customerFields" | "professorFields",
  field: string,
) => {
  return appSettings?.[type]?.[field] || "optional";
};

const FieldWrapper = ({
  appSettings,
  type,
  field,
  label,
  children,
  ...props
}: {
  appSettings: any;
  type: "customerFields" | "professorFields";
  field: string;
  label: string;
  children: (status: string) => React.ReactNode;
  [key: string]: any;
}) => {
  const status = getFieldConfig(appSettings, type, field);
  if (status === "hidden") return null;
  return (
    <div {...props}>
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          color: "var(--text-secondary)",
        }}
      >
        {label} {status === "mandatory" && "*"}
      </label>
      {children(status)}
    </div>
  );
};

// Utilitário para formatação de moeda
export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export const getSkuForStringHelper = (sName: string, settings: any) => {
  if (!settings || !settings.strings) return "PESO-GRIP";
  const sObj = settings.strings.find((s: any) => (typeof s === "string" ? s : s.name) === sName);
  return sObj && typeof sObj !== "string" ? sObj.sku || "PESO-GRIP" : "PESO-GRIP";
};

export const getJobOrderItems = (job: any, appSettings: any) => {
  const items = [];
  let customPrice = 0;
  let hasCustomization = false;

  if (Array.isArray(job.auxServices)) {
    job.auxServices.forEach((aux: any) => {
      if (
        aux.isActive ||
        (aux.price && aux.price > 0) ||
        (aux.notes && aux.notes !== "Não" && aux.notes.trim() !== "")
      ) {
        hasCustomization = true;
        const discountPercent = Number(aux.discountPercent) || 0;
        const discountValue = Number(aux.discountValue) || 0;
        customPrice += (Number(aux.price) || 0) * (1 - discountPercent / 100) - discountValue;
      }
    });
  }

  const orderCode = job.orderCode || job.id;

  if (job.isStringing) {
    const stringPrice = Math.max(0, (job.price || 0) - customPrice);
    const mString = job.stringMains || job.mainString || "Não definido";
    const cString = job.stringCross || job.crossString || mString;

    if (job.isHybrid && mString !== cString) {
      let descMains = `Ordem TT: ${orderCode} - Corda Main ${mString}`;
      if (descMains.length > 120) descMains = descMains.substring(0, 117) + "...";
      let descCrosses = `Ordem TT: ${orderCode} - Corda Cross ${cString}`;
      if (descCrosses.length > 120) descCrosses = descCrosses.substring(0, 117) + "...";

      items.push({
        id: job.id + "-M",
        sku: getSkuForStringHelper(mString, appSettings),
        description: descMains,
        quantity: 0.5,
        price: stringPrice,
      });
      items.push({
        id: job.id + "-C",
        sku: getSkuForStringHelper(cString, appSettings),
        description: descCrosses,
        quantity: 0.5,
        price: stringPrice,
      });
    } else {
      const str = job.isHybrid ? `${mString}/${cString}` : mString;
      let desc = `Ordem TT: ${orderCode} - Corda (${str})`;
      if (desc.length > 120) desc = desc.substring(0, 117) + "...";
      items.push({
        id: job.id,
        sku: getSkuForStringHelper(mString, appSettings),
        description: desc,
        quantity: 1,
        price: stringPrice,
      });
    }
  }

  if (hasCustomization || !job.isStringing) {
    const pesoPrice = !job.isStringing ? (job.price || 0) : customPrice;
    let desc = `Ordem TT: ${orderCode} - Peso e/ou Grip`;
    items.push({
      id: job.id + (job.isStringing ? "-AUX" : ""),
      sku: "PESO-GRIP",
      description: desc,
      quantity: 1,
      price: pesoPrice,
    });
  }

  return items;
};

export const StringerDashboard = () => {
  const { currentUser, acceptLgpd, logout } = useAuth();
  const [showLgpdModal, setShowLgpdModal] = useState(false);
  const [isAcceptingLgpd, setIsAcceptingLgpd] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState<"privacy" | "terms">(
    "privacy",
  );

  useEffect(() => {
    if (
      currentUser &&
      !currentUser.lgpdAcceptedAt &&
      currentUser.role !== "ADMIN"
    ) {
      setShowLgpdModal(true);
    }
  }, [currentUser]);

  const handleAcceptLgpd = async () => {
    setIsAcceptingLgpd(true);
    try {
      await acceptLgpd();
      setShowLgpdModal(false);
    } catch (e) {
      alert("Erro ao salvar aceite. Tente novamente.");
    } finally {
      setIsAcceptingLgpd(false);
    }
  };

  const handleDeclineLgpd = () => {
    logout();
    navigate("/");
  };
  const navigate = useNavigate();
  const [view, setView] = useState<
    | "dashboard"
    | "new_job"
    | "customers"
    | "professors"
    | "stringing"
    | "order_details"
    | "analytics"
    | "orders"
    | "settings"
  >("dashboard");
  const [activeOrderJob, setActiveOrderJob] = useState<any>(null);
  const [activeStringingJob, setActiveStringingJob] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState<
    "all" | "to_string" | "picking_up" | "picked_up"
  >("all");
  const [dateFilter, setDateFilter] = useState<
    "all" | "today" | "week" | "month" | "year"
  >("all");
  const [jobSearchQuery, setJobSearchQuery] = useState("");
  const [jobCurrentPage, setJobCurrentPage] = useState(1);
  const jobsPerPage = 10;
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerSyncStatusData, setCustomerSyncStatusData] = useState<{
    syncStatus: string;
    syncErrors: string | null;
  } | null>(null);
  const [cpfCnpjError, setCpfCnpjError] = useState(false);
  const [isProfessorModalOpen, setIsProfessorModalOpen] = useState(false);
  const [selectedProfessor, setSelectedProfessor] = useState<any>(null);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [activeFeedbackJob, setActiveFeedbackJob] = useState<any>(null);
  const [isRacketModalOpen, setIsRacketModalOpen] = useState(false);
  const [isCloneRacketModalOpen, setIsCloneRacketModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [activePaymentJob, setActivePaymentJob] = useState<any>(null);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentCode, setPaymentCode] = useState("");
  const [paymentMode, setPaymentMode] = useState<"order" | "racket">("order");
  const [currentOrderCode, setCurrentOrderCode] = useState<string>("");

  const generateUniqueAlphanumericCode = (existingJobs: any[]) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    let isUnique = false;
    while (!isUnique) {
      code = "";
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      isUnique = !existingJobs.some((j: any) => j.orderCode === code);
    }
    return code;
  };
  const [activePickupJob, setActivePickupJob] = useState<any>(null);
  const [pickupPaymentMode, setPickupPaymentMode] = useState<"single" | "order" | null>(null);
  const [pickupPaymentCode, setPickupPaymentCode] = useState("");
  const [racketFormDefault, setRacketFormDefault] = useState<any | null>(null);
  const [selectedJobRacket, setSelectedJobRacket] = useState("");
  const [newJobStep, setNewJobStep] = useState<1 | 2>(1);
  const [commissionedProfessorId, setCommissionedProfessorId] =
    useState<string>("");
  const [commissionAction, setCommissionAction] = useState<string>("");

  // Persistent States
  const [customers, setCustomers] = useState<any[]>([]);
  const [customerFilters, setCustomerFilters] = useState({
    name: "",
    numericId: "",
    originClub: "",
    professorId: "",
    email: "",
    phone: "",
    stringingPoint: "",
  });
  const [isCustomerSearchModalOpen, setIsCustomerSearchModalOpen] =
    useState(false);
  const activeCustomerFiltersCount = Object.values(customerFilters).filter(
    (v) => typeof v === "string" && v.trim() !== "",
  ).length;
  const [customerItemsPerPage, setCustomerItemsPerPage] = useState<
    number | "all"
  >(10);
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const [professorFilters, setProfessorFilters] = useState({
    name: "",
    numericId: "",
    email: "",
    phone: "",
  });
  const [isProfessorSearchModalOpen, setIsProfessorSearchModalOpen] =
    useState(false);
  const activeProfessorFiltersCount = Object.values(professorFilters).filter(
    (v) => typeof v === "string" && v.trim() !== "",
  ).length;
  const [professorItemsPerPage, setProfessorItemsPerPage] = useState<
    number | "all"
  >(10);
  const [professorCurrentPage, setProfessorCurrentPage] = useState(1);

  useEffect(() => {
    setCustomerCurrentPage(1);
  }, [customerFilters]);

  useEffect(() => {
    setProfessorCurrentPage(1);
  }, [professorFilters]);

  useEffect(() => {
    setJobCurrentPage(1);
  }, [activeFilter, dateFilter, jobSearchQuery]);

  const [professors, setProfessors] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedPointFilter, setSelectedPointFilter] = useState("Todos");
  const [availablePoints, setAvailablePoints] = useState<string[]>([]);

  const API_URL =
    import.meta.env.VITE_API_URL || "https://techtennis-api.vercel.app";
  const getAuthHeader = () => {
    const token = localStorage.getItem("tt_auth_token");
    return { Authorization: `Bearer ${token}` };
  };

  const handleAuthError = (res: Response) => {
    if (res.status === 401 || res.status === 403) {
      alert("Sua sessão expirou por tempo inativo. Faça login novamente.");
      localStorage.removeItem("tt_auth_token");
      window.location.href = "/";
      return true;
    }
    return false;
  };

  const handleNotifyPickup = async (jobId: string, target: 'cliente' | 'professor') => {
    try {
      const res = await fetch(`${API_URL}/api/jobs/${jobId}/notify-pickup`, {
        method: "POST",
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ target })
      });
      if (handleAuthError(res)) return;
      if (!res.ok) {
        const errorData = await res.json();
        alert(errorData.error || "Erro ao enviar notificação.");
        return;
      }
      alert("Notificação enviada com sucesso pelo App TT!");
    } catch (err) {
      console.error(err);
      alert("Erro ao conectar com o servidor.");
    }
  };

  useEffect(() => {
    const fetchPoints = async () => {
      try {
        if (currentUser?.role === "ADMIN") {
          const res = await fetch(`${API_URL}/api/settings/stringer-allocation`, { headers: getAuthHeader() });
          if (res.ok) {
            const data = await res.json();
            setAvailablePoints(data.availablePoints || []);
          }
        }
      } catch (e) {}
    };
    fetchPoints();
  }, [currentUser]);
  
  useEffect(() => {
    fetchJobs();
  }, [selectedPointFilter]);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/jobs?stringingPoint=${encodeURIComponent(selectedPointFilter)}`, {
        headers: getAuthHeader(),
      });
      if (handleAuthError(res)) return;
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRackets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/rackets`, {
        headers: getAuthHeader(),
      });
      if (handleAuthError(res)) return;
      if (res.ok) setRackets(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const [allSyncStatuses, setAllSyncStatuses] = useState<
    Record<string, { syncStatus: string; syncErrors: string | null }>
  >({});

  const fetchSyncStatuses = async () => {
    if (!import.meta.env.VITE_BFF_URL) return;
    try {
      const res = await fetch(
        `${import.meta.env.VITE_BFF_URL}/api/clientes/sync-status`,
      );
      if (res.ok) {
        const statuses = await res.json();
        const map: Record<string, any> = {};
        statuses.forEach((s: any) => {
          map[s.id] = s;
        });
        setAllSyncStatuses(map);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/customers`, {
        headers: getAuthHeader(),
      });
      if (handleAuthError(res)) return;
      if (res.ok) {
        const data = await res.json();
        setCustomers(
          data.sort((a: any, b: any) =>
            (a.name || "").localeCompare(b.name || "", "pt-BR", {
              sensitivity: "base",
            }),
          ),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProfessors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/professors`, {
        headers: getAuthHeader(),
      });
      if (handleAuthError(res)) return;
      if (res.ok) {
        const data = await res.json();
        setProfessors(
          data.sort((a: any, b: any) =>
            (a.name || "").localeCompare(b.name || "", "pt-BR", {
              sensitivity: "base",
            }),
          ),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings`, {
        headers: getAuthHeader(),
      });
      if (handleAuthError(res)) return;
      if (res.ok) {
        const data = await res.json();
        if (Object.keys(data).length > 0) setAppSettings(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchCustomers();
    fetchRackets();
    fetchProfessors();
    fetchSettings();
    fetchSyncStatuses();
  }, []);

  const [rackets, setRackets] = useState<any[]>([]);

  const [appSettings, setAppSettings] = useState<any>({
    strings: [
      "Solinco Hyper-G Green 115",
      "Babolat RPM Blast",
      "Luxilon Alu Power",
    ],
    pickupPoints: ["Test", "Loja 1"],
    machines: ["Babolat Star 5", "Wilson Baiardo"],
    stringers: ["Tester Ernesto", "Paulo Souto"],
    sports: ["Tênis", "Beach Tennis", "Squash", "Badminton", "Padel"],
    clubs: [],
  });
  const [initialSettingsLoaded, setInitialSettingsLoaded] = useState(false);

  useEffect(() => {
    if (!initialSettingsLoaded) {
      setInitialSettingsLoaded(true);
      return;
    }
    fetch(`${API_URL}/api/settings`, {
      method: "PUT",
      headers: { ...getAuthHeader(), "Content-Type": "application/json" },
      body: JSON.stringify(appSettings),
    }).catch((e) => console.error(e));
  }, [appSettings]);

  // Search State
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerError, setCustomerError] = useState(false);

  useEffect(() => {
    const targetEntity = isCustomerModalOpen
      ? selectedCustomer
      : isProfessorModalOpen
        ? selectedProfessor
        : null;
    if (targetEntity?.id && import.meta.env.VITE_BFF_URL) {
      fetch(
        `${import.meta.env.VITE_BFF_URL}/api/clientes/${targetEntity.id}/status`,
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data)
            setCustomerSyncStatusData({
              syncStatus: data.syncStatus,
              syncErrors: data.syncErrors,
            });
        })
        .catch(console.error);
    } else {
      setCustomerSyncStatusData(null);
    }
  }, [
    isCustomerModalOpen,
    selectedCustomer,
    isProfessorModalOpen,
    selectedProfessor,
  ]);

  // Form State
  const [jobSaved, setJobSaved] = useState(false);
  const [isSavingMultiple, setIsSavingMultiple] = useState(false);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [mainString, setMainString] = useState("");
  const [tensionMain, setTensionMain] = useState<number | "">("");
  const [isHybrid, setIsHybrid] = useState(false);
  const [crossString, setCrossString] = useState("");
  const [tensionCross, setTensionCross] = useState<number | "">("");
  const [isStringing, setIsStringing] = useState(true);
  const [preStretchMain, setPreStretchMain] = useState("");
  const [preStretchCross, setPreStretchCross] = useState("");
  const [stringingType, setStringingType] = useState("Não definido");
  const [tensionUnit, setTensionUnit] = useState("Lbs");
  const [dashboardStringer, setDashboardStringer] = useState("");
  const [price, setPrice] = useState<number | "">("");
  const [priceDiscountPercent, setPriceDiscountPercent] = useState<number | "">(
    "",
  );
  const [priceDiscountValue, setPriceDiscountValue] = useState<number | "">("");
  const [auxServices, setAuxServices] = useState<
    {
      type: string;
      isActive: boolean;
      price: number;
      discountPercent: number | "";
      discountValue: number | "";
      notes: string;
    }[]
  >([
    {
      type: "Trocar grip base",
      isActive: false,
      price: 0,
      discountPercent: "",
      discountValue: "",
      notes: "",
    },
    {
      type: "Trocar overgrip",
      isActive: false,
      price: 0,
      discountPercent: "",
      discountValue: "",
      notes: "",
    },
    {
      type: "Serviço customizado",
      isActive: false,
      price: 0,
      discountPercent: "",
      discountValue: "",
      notes: "",
    },
    {
      type: "Compra de raquete nova",
      isActive: false,
      price: 0,
      discountPercent: "",
      discountValue: "",
      notes: "",
    },
    {
      type: "Outros serviços",
      isActive: false,
      price: 0,
      discountPercent: "",
      discountValue: "",
      notes: "",
    },
  ]);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupNotes, setPickupNotes] = useState("");
  const [jobStringingPoint, setJobStringingPoint] = useState("");
  const [hasOwnReel, setHasOwnReel] = useState(false);
  const [hasOwnSet, setHasOwnSet] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoNotes, setLogoNotes] = useState("");
  const [racketNotes, setRacketNotes] = useState("");

  // Pontos de encordoamento disponíveis para o usuário atual:
  // ADMIN (ex.: Ernesto Mori ou Paulo Souto): todos os pontos existentes no sistema.
  // ENCORDOADOR: apenas os pontos onde ele estiver alocado na Alocação Diária.
  const availablePointsForUser = useMemo(() => {
    const allPoints = [...(appSettings.pickupPoints || [])].sort((a: any, b: any) =>
      String(typeof a === 'string' ? a : a.name).localeCompare(
        String(typeof b === 'string' ? b : b.name),
        undefined,
        { sensitivity: 'base' }
      )
    ).map((p: any) => (typeof p === 'string' ? p : p.name));

    if (currentUser?.role === 'ADMIN') {
      return allPoints;
    }

    const allocs = appSettings.stringerAllocations || {};
    const norm = (s: string) =>
      (s || '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    let allocated: string[] = [];
    const targetName = currentUser?.name ? norm(currentUser.name) : '';
    if (targetName) {
      for (const [key, val] of Object.entries(allocs)) {
        if (norm(key) === targetName && Array.isArray(val)) {
          allocated = val as string[];
          break;
        }
      }
    }

    if (allocated.length === 0 && dashboardStringer) {
      const normDash = norm(dashboardStringer);
      for (const [key, val] of Object.entries(allocs)) {
        if (norm(key) === normDash && Array.isArray(val)) {
          allocated = val as string[];
          break;
        }
      }
    }

    if (allocated.length === 0 && currentUser) {
      if (currentUser.id && Array.isArray(allocs[currentUser.id])) {
        allocated = allocs[currentUser.id];
      } else if (currentUser.email && Array.isArray(allocs[currentUser.email])) {
        allocated = allocs[currentUser.email];
      }
    }

    if (allocated.length === 0 && Array.isArray(currentUser?.stringingPoints)) {
      allocated = currentUser.stringingPoints;
    }

    return allPoints.filter((p: string) => allocated.includes(p));
  }, [appSettings.pickupPoints, appSettings.stringerAllocations, currentUser, dashboardStringer]);

  // Apply Club Discount rules
  useEffect(() => {
    if (editingJobId) return; // Não sobrescreve descontos ao editar uma job
    if (selectedCustomer?.originClub && appSettings.clubDiscounts) {
      const getDiscount = (service: string) => {
        const todayStr = new Date().toISOString().split("T")[0];
        const originClub = (selectedCustomer.originClub || "")
          .trim()
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        for (const d of appSettings.clubDiscounts) {
          if (
            d.club &&
            d.club.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === originClub &&
            (d.service === service || d.service === "Todos")
          ) {
            if (!d.startDate && !d.endDate) return d;
            if (d.startDate && todayStr < d.startDate) continue;
            if (d.endDate && todayStr > d.endDate) continue;
            return d;
          }
        }
        return null;
      };
      const mainD = getDiscount("Encordoamento");
      setPriceDiscountPercent(mainD ? mainD.percent : "");
      setPriceDiscountValue(mainD ? mainD.value : "");
      setAuxServices((prev) =>
        prev.map((s) => {
          const d = getDiscount(s.type);
          return {
            ...s,
            discountPercent: d ? d.percent : "",
            discountValue: d ? d.value : "",
          };
        }),
      );
    } else {
      setPriceDiscountPercent("");
      setPriceDiscountValue("");
      setAuxServices((prev) =>
        prev.map((s) => ({ ...s, discountPercent: "", discountValue: "" })),
      );
    }
  }, [selectedCustomer, appSettings?.clubDiscounts]);

  // Dynamic string pricing
  useEffect(() => {
    if (!isStringing) return;
    let computedPrice = 0;

    const getStringObj = (sName: string) => {
      return appSettings.strings?.find(
        (s: any) => (typeof s === "string" ? s : s.name) === sName,
      );
    };

    const getStringPrice = (sName: string) => {
      const sObj = getStringObj(sName);
      if (sObj && typeof sObj !== "string") {
        return sObj.price || 0;
      }
      return 0;
    };

    const getStringingPrice = (type: string) => {
      const sp = appSettings.stringingPrices?.find(
        (sp: any) => sp.type === type,
      );
      return sp ? sp.price : 0;
    };

    if (mainString) {
      if (hasOwnReel || hasOwnSet) {
        const mObj = getStringObj(mainString);
        const mType =
          mObj && typeof mObj !== "string" ? mObj.type : "Monofilamento";

        if (isHybrid && crossString && mainString !== crossString) {
          const cObj = getStringObj(crossString);
          const cType =
            cObj && typeof cObj !== "string" ? cObj.type : "Monofilamento";
          computedPrice =
            getStringingPrice(mType) / 2 + getStringingPrice(cType) / 2;
        } else {
          computedPrice = getStringingPrice(mType);
        }
      } else {
        const mPrice = getStringPrice(mainString);
        if (isHybrid && crossString && mainString !== crossString) {
          const cPrice = getStringPrice(crossString);
          computedPrice = mPrice / 2 + cPrice / 2;
        } else {
          computedPrice = mPrice;
        }
      }
    }

    setPrice(computedPrice);
  }, [
    mainString,
    crossString,
    isHybrid,
    appSettings.strings,
    appSettings.stringingPrices,
    isStringing,
    hasOwnReel,
    hasOwnSet,
  ]);

  let filteredJobs =
    activeFilter === "all"
      ? jobs
      : jobs.filter((job) => {
          if (activeFilter === "to_string" && job.type === "stringing")
            return true;
          return job.type === activeFilter;
        });

  if (dateFilter === "today") {
    filteredJobs = filteredJobs.filter((job) => {
      if (!job.pickupDate) return false;
      const d = new Date();
      const todayLocal =
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0");
      return job.pickupDate.split("T")[0] === todayLocal;
    });
  } else if (dateFilter === "week") {
    filteredJobs = filteredJobs.filter((job) => {
      if (!job.pickupDate) return false;
      const d = new Date();
      const jobDate = new Date(job.pickupDate);
      const diffTime = Math.abs(d.getTime() - jobDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7;
    });
  } else if (dateFilter === "month") {
    filteredJobs = filteredJobs.filter((job) => {
      if (!job.pickupDate) return false;
      const d = new Date();
      const jobDate = new Date(job.pickupDate);
      return (
        jobDate.getMonth() === d.getMonth() &&
        jobDate.getFullYear() === d.getFullYear()
      );
    });
  } else if (dateFilter === "year") {
    filteredJobs = filteredJobs.filter((job) => {
      if (!job.pickupDate) return false;
      const d = new Date();
      const jobDate = new Date(job.pickupDate);
      return jobDate.getFullYear() === d.getFullYear();
    });
  }

  if (jobSearchQuery) {
    const q = jobSearchQuery.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    filteredJobs = filteredJobs.filter(
      (job) =>
        (job.customerName || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(q) ||
        (job.customerPhone || "").includes(q),
    );
  }

  const totalFilteredJobs = filteredJobs.length;
  const totalJobPages = Math.ceil(totalFilteredJobs / jobsPerPage);

  filteredJobs = [...filteredJobs].sort((a, b) => {
    const da = a.pickupDate ? new Date(a.pickupDate).getTime() : Infinity;
    const db = b.pickupDate ? new Date(b.pickupDate).getTime() : Infinity;
    return da - db;
  });

  const paginatedJobs = filteredJobs.slice(
    (jobCurrentPage - 1) * jobsPerPage,
    jobCurrentPage * jobsPerPage,
  );

  const exportDashboardData = (format: "csv" | "excel" | "pdf") => {
    if (format === "pdf") {
      window.print();
      return;
    }
    const headers = [
      "Data",
      "Retirada",
      "Cliente",
      "Raquete",
      "Corda/Tensão",
      "Status",
      "Preço",
    ];
    const rows = filteredJobs.map((o: any) => [
      o.date,
      o.pickupDate ? new Date(o.pickupDate).toLocaleString("pt-BR") : "",
      o.customerName,
      o.racketModel,
      `${o.stringMains || o.mainString || ""} @${o.tension || ""}`,
      o.type === "picked_up"
        ? "Entregue"
        : o.type === "picking_up"
          ? "Pronta"
          : o.type === "to_string"
            ? "Para Encordoar"
            : "Aguardando",
      Number(o.price != null && o.price !== "" ? o.price : 120).toFixed(2),
    ]);
    const csvContent = [
      headers.map((h) => `"${h}"`).join(";"),
      ...rows.map((r) =>
        r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"),
      ),
    ].join("\n");
    let mimeType = "text/csv";
    let ext = ".csv";
    if (format === "excel") {
      mimeType = "application/vnd.ms-excel";
      ext = ".xls";
    }
    const blob = new Blob(["\ufeff" + csvContent], {
      type: `${mimeType};charset=utf-8;`,
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `fila_encordoamento_${new Date().toISOString().split("T")[0]}${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCustomersData = (format: "csv" | "excel" | "pdf") => {
    if (format === "pdf") {
      window.print();
      return;
    }
    const headers = [
      "Nome",
      "ID TechTennis",
      "Gênero",
      "Ponto de Encordoamento",
      "Professor",
      "Clube",
      "E-mail",
      "Celular",
    ];
    const rows = customers.map((c: any) => [
      c.name || "",
      c.numericId || "",
      c.gender === "F" ? "Feminino" : c.gender === "M" ? "Masculino" : "Outro",
      c.stringingPoint || "Loja 1 - LaVille Mall",
      professors.find((p: any) => p.id === c.professorId)?.name ||
        "Não informado",
      c.originClub || "Não informado",
      c.email || "Não informado",
      c.phone || "",
    ]);
    const csvContent = [
      headers.map((h) => `"${h}"`).join(";"),
      ...rows.map((r) =>
        r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"),
      ),
    ].join("\n");
    let mimeType = "text/csv";
    let ext = ".csv";
    if (format === "excel") {
      mimeType = "application/vnd.ms-excel";
      ext = ".xls";
    }
    const blob = new Blob(["\ufeff" + csvContent], {
      type: `${mimeType};charset=utf-8;`,
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `base_clientes_${new Date().toISOString().split("T")[0]}${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportProfessorsData = (format: "csv" | "excel" | "pdf") => {
    if (format === "pdf") {
      window.print();
      return;
    }
    const headers = ["Nome", "ID TechTennis", "E-mail", "Celular"];
    const rows = professors.map((p: any) => [
      p.name || "",
      p.numericId || "",
      p.email || "",
      p.phone || "",
    ]);
    const csvContent = [
      headers.map((h) => `"${h}"`).join(";"),
      ...rows.map((r) =>
        r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"),
      ),
    ].join("\n");
    let mimeType = "text/csv";
    let ext = ".csv";
    if (format === "excel") {
      mimeType = "application/vnd.ms-excel";
      ext = ".xls";
    }
    const blob = new Blob(["\ufeff" + csvContent], {
      type: `${mimeType};charset=utf-8;`,
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `professores_comissionados_${new Date().toISOString().split("T")[0]}${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate computed discounts and final price
  const basePriceValue = Number(price) || 0;
  const baseDiscountVal = Number(priceDiscountPercent) || 0;
  const baseDiscountAmt = Number(priceDiscountValue) || 0;
  const finalBasePrice =
    basePriceValue * (1 - baseDiscountVal / 100) - baseDiscountAmt;

  const finalAuxPrice = auxServices
    .filter((s) => s.isActive)
    .reduce((acc, s) => {
      const sDiscount = Number(s.discountPercent) || 0;
      const sDiscountVal = Number(s.discountValue) || 0;
      return acc + (s.price * (1 - sDiscount / 100) - sDiscountVal);
    }, 0);

  const displayFinalPrice = (finalBasePrice + finalAuxPrice).toFixed(2);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const newCode = currentOrderCode || generateUniqueAlphanumericCode(jobs);
    if (!currentOrderCode) setCurrentOrderCode(newCode);

    const finalPrice = finalBasePrice + finalAuxPrice;

    const newJob = {
      id: editingJobId ? editingJobId : Date.now().toString(),
      orderCode: newCode,
      customerId: selectedCustomer ? selectedCustomer.id : undefined,
      customerName: selectedCustomer ? selectedCustomer.name : "Desconhecido",
      racketModel:
        rackets.find((r) => r.id === selectedJobRacket)?.name ||
        "Raquete Customizada",
      date: new Date().toLocaleDateString("pt-BR"),
      tension: isHybrid
        ? `${tensionMain}/${tensionCross} ${tensionUnit}`
        : `${tensionMain} ${tensionUnit}`,
      status: "aguardando",
      type: "to_string" as any,
      mainString,
      crossString,
      tensionMain,
      tensionCross,
      isHybrid,
      racketId: selectedJobRacket,
      isStringing,
      stringingType,
      tensionUnit,
      preStretchMain,
      preStretchCross,
      basePrice: Number(price),
      priceDiscountPercent:
        priceDiscountPercent === "" ? 0 : Number(priceDiscountPercent),
      priceDiscountValue:
        priceDiscountValue === "" ? 0 : Number(priceDiscountValue),
      price: finalPrice,
      pickupDate,
      commissionedProfessorId: commissionedProfessorId || null,
      commissionAction: commissionedProfessorId ? commissionAction : null,
      auxServices,
      stringerName: dashboardStringer,
      pickupNotes,
      stringingPoint: jobStringingPoint,
      hasOwnReel,
      hasOwnSet,
      hasLogo,
      logoNotes,
      racketNotes,
    };
    // Envia para API
    fetch(
      editingJobId
        ? `${API_URL}/api/jobs/${editingJobId}`
        : `${API_URL}/api/jobs`,
      {
        method: editingJobId ? "PUT" : "POST",
        headers: { ...getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(newJob),
      },
    )
      .then(async (res) => {
        if (!res.ok) throw new Error("Erro ao salvar job");
        return res.json();
      })
      .then(async (savedJob) => {
          fetchJobs();
  
          const finalJob = savedJob && savedJob.id ? savedJob : newJob;

          // Sincroniza o cliente com o BFF ANTES de criar/atualizar a ordem
          if (import.meta.env.VITE_BFF_URL && newJob.customerId) {
            const custToSync = selectedCustomer || customers.find(c => c.id === newJob.customerId);
            if (custToSync) {
               try {
                 await fetch(`${import.meta.env.VITE_BFF_URL}/api/clientes/${custToSync.id}`, {
                   method: 'PUT',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify({
                      name: custToSync.name,
                      numericId: custToSync.numericId,
                      email: custToSync.email,
                      phone: custToSync.phone,
                      document: custToSync.cpfCnpj,
                      address: custToSync.address,
                      addressNumber: custToSync.addressNumber,
                      complement: custToSync.complement,
                      neighborhood: custToSync.neighborhood,
                      city: custToSync.city,
                      state: custToSync.state,
                      zipCode: custToSync.zipCode
                   })
                 });
               } catch (e) {
                 console.error("Erro ao sincronizar cliente antes da ordem:", e);
               }
            }
          }
          const allJobsForOrder = [
          ...jobs.filter((j) => j.orderCode === newCode && j.id !== newJob.id),
          finalJob,
        ];

        const orderItems = allJobsForOrder.flatMap((job) => getJobOrderItems(job, appSettings));

        const orderTotal = allJobsForOrder.reduce(
          (acc, job) => acc + job.price,
          0,
        );

        // Sincroniza Pedido com BFF
        const isNewOrder = !editingJobId && allJobsForOrder.length === 1; // Só é POST se for a primeira raquete da ordem e não for edição

        if (isNewOrder && import.meta.env.VITE_BFF_URL && newJob.customerId) {
          fetch(`${import.meta.env.VITE_BFF_URL}/api/pedidos`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: newCode,
              customerId: newJob.customerId,
              customerName: newJob.customerName,
              customerPhone: selectedCustomer?.phone || "",
              customerEmail: selectedCustomer?.email || "",
              customerDocument: selectedCustomer?.document || "",
              customerAddress: selectedCustomer?.address || "",
              customerAddressNumber: selectedCustomer?.addressNumber || "",
              customerComplement: selectedCustomer?.complement || "",
              customerNeighborhood: selectedCustomer?.neighborhood || "",
              customerCity: selectedCustomer?.city || "",
              customerState: selectedCustomer?.state || "",
              customerZipCode: selectedCustomer?.zipCode || "",
              total: orderTotal,
              items: orderItems,
            }),
          }).catch((err) =>
            console.error("Erro ao sincronizar pedido com BFF:", err),
          );
        } else if (
          !isNewOrder &&
          import.meta.env.VITE_BFF_URL &&
          newJob.customerId
        ) {
          // Para edições ou adições de nova raquete à mesma ordem, fazemos PUT
          fetch(`${import.meta.env.VITE_BFF_URL}/api/pedidos/${newCode}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: newCode,
              customerId: newJob.customerId,
              customerName: newJob.customerName,
              customerPhone: selectedCustomer?.phone || "",
              customerEmail: selectedCustomer?.email || "",
              customerDocument: selectedCustomer?.document || "",
              customerAddress: selectedCustomer?.address || "",
              customerAddressNumber: selectedCustomer?.addressNumber || "",
              customerComplement: selectedCustomer?.complement || "",
              customerNeighborhood: selectedCustomer?.neighborhood || "",
              customerCity: selectedCustomer?.city || "",
              customerState: selectedCustomer?.state || "",
              customerZipCode: selectedCustomer?.zipCode || "",
              total: orderTotal,
              items: orderItems,
            }),
          }).catch((err) =>
            console.error("Erro ao atualizar pedido com BFF:", err),
          );
        }
      })
      .catch((err) => {
        console.error("Falha ao salvar job", err);
        alert("Falha ao salvar job. Verifique a conexão.");
      });

    setJobSaved(true);
    setTimeout(() => {
      setJobSaved(false);
      setView("dashboard");
      setNewJobStep(1);
      setCurrentOrderCode("");
      resetForm();
    }, 1500);
  };

  const resetForm = () => {
    setEditingJobId(null);
    setSelectedCustomer(null);
    setCommissionedProfessorId("");
    setCommissionAction("");
    setCustomerQuery("");
    setSelectedJobRacket("");
    setMainString("");
    setCrossString("");
    setTensionMain("");
    setTensionCross("");
    setIsHybrid(false);
    setIsStringing(true);
    setPreStretchMain("");
    setPreStretchCross("");
    setPrice("");
    setPriceDiscountPercent("");
    setPriceDiscountValue("");
    setAuxServices([
      {
        type: "Trocar grip base",
        isActive: false,
        price: 0,
        discountPercent: "",
        discountValue: "",
        notes: "",
      },
      {
        type: "Trocar overgrip",
        isActive: false,
        price: 0,
        discountPercent: "",
        discountValue: "",
        notes: "",
      },
      {
        type: "Serviço customizado",
        isActive: false,
        price: 0,
        discountPercent: "",
        discountValue: "",
        notes: "",
      },
      {
        type: "Compra de raquete nova",
        isActive: false,
        price: 0,
        discountPercent: "",
        discountValue: "",
        notes: "",
      },
      {
        type: "Outros serviços",
        isActive: false,
        price: 0,
        discountPercent: "",
        discountValue: "",
        notes: "",
      },
    ]);
    setPickupDate("");
    const defaultStringer = currentUser?.role !== "ADMIN" && currentUser?.name ? currentUser.name : "";
    setDashboardStringer(defaultStringer);
    setPickupNotes("");
    const defaultPt = currentUser?.role !== "ADMIN" && availablePointsForUser.length === 1 ? availablePointsForUser[0] : "";
    setJobStringingPoint(defaultPt);
    setHasOwnReel(false);
    setHasOwnSet(false);
    setHasLogo(false);
    setLogoNotes("");
    setRacketNotes("");
  };

  const startEditingJob = (job: any, cust: any) => {
    setSelectedCustomer(cust);
    setCustomerQuery(cust?.name || "");
    setEditingJobId(job.id);
    setCurrentOrderCode(job.orderCode || job.id.substring(0, 8).toUpperCase());

    const racketId = job.racketId || rackets.find(
      (r) => r.name === job.racketModel && r.customerId === cust?.id,
    )?.id;
    if (racketId) setSelectedJobRacket(racketId);

    setMainString(job.stringMains || job.mainString || "");
    setCrossString(job.stringCross || job.crossString || "");
    setIsHybrid(job.isHybrid || false);
    setIsStringing(job.isStringing !== false);
    setPreStretchMain(job.preStretchMain || "");
    setPreStretchCross(job.preStretchCross || "");

    const match = job.tension?.match(/(\d+)(?:\/(\d+))?/);
    if (match) {
      setTensionMain(parseInt(match[1]) || "");
      if (match[2]) {
        setIsHybrid(true);
        setTensionCross(parseInt(match[2]));
      } else {
        setTensionCross(parseInt(match[1]) || "");
      }
    } else {
      setTensionMain("");
      setTensionCross("");
    }

    if (job.basePrice !== undefined) {
      setPrice(job.basePrice);
    } else if (job.price !== undefined) {
      setPrice(job.price);
    } else {
      setPrice("");
    }

    setPriceDiscountPercent(
      job.priceDiscountPercent !== undefined ? job.priceDiscountPercent : "",
    );
    setPriceDiscountValue(
      job.priceDiscountValue !== undefined ? job.priceDiscountValue : "",
    );

    if (job.auxServices) {
      setAuxServices(job.auxServices);
    } else {
      setAuxServices([
        {
          type: "Trocar grip base",
          isActive: false,
          price: 0,
          discountPercent: "",
          discountValue: "",
          notes: "",
        },
        {
          type: "Trocar overgrip",
          isActive: false,
          price: 0,
          discountPercent: "",
          discountValue: "",
          notes: "",
        },
        {
          type: "Serviço customizado",
          isActive: false,
          price: 0,
          discountPercent: "",
          discountValue: "",
          notes: "",
        },
        {
          type: "Compra de raquete nova",
          isActive: false,
          price: 0,
          discountPercent: "",
          discountValue: "",
          notes: "",
        },
        {
          type: "Outros serviços",
          isActive: false,
          price: 0,
          discountPercent: "",
          discountValue: "",
          notes: "",
        },
      ]);
    }

    setPickupDate(job.pickupDate || "");
    setCommissionedProfessorId(job.commissionedProfessorId || "");
    setCommissionAction(job.commissionAction || "");
    setDashboardStringer(job.stringerName || "");
    setPickupNotes(job.pickupNotes || "");
    setJobStringingPoint(job.stringingPoint || "");
    setHasOwnReel(job.hasOwnReel ?? false);
    setHasOwnSet(job.hasOwnSet ?? false);
    setHasLogo(job.hasLogo ?? false);
    setLogoNotes(job.logoNotes || "");
    setRacketNotes(job.racketNotes || "");
    setView("new_job");
    setNewJobStep(2);
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case "dropping_off":
        return "#D93B65"; // Reddish pink
      case "to_string":
        return "#F2C94C"; // Yellow
      case "picking_up":
        return "#6FCF97"; // Green
      default:
        return "var(--primary-color)";
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.1)",
    border: "1px solid var(--border-light)",
    color: "white",
  };

  return (
    <div
      style={{
        paddingTop: "140px",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <StringerAllocationModal onSuccess={fetchSettings} />
      {/* Top Navbar / Tabs (Dashboard specific) */}
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          padding: "0 24px",
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
          overflowX: "auto",
          borderBottom: "1px solid var(--border-light)",
        }}
      >
        <button
          onClick={() => navigate("/")}
          style={{
            background: "var(--bg-panel)",
            border: "none",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            color: "var(--text-primary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <button
          onClick={() => {
            setView("dashboard");
            setNewJobStep(1);
            setSelectedCustomer(null);
            setCustomerQuery("");
            setSelectedJobRacket("");
            setCurrentOrderCode("");
            setEditingJobId(null);
          }}
          style={{
            background: "none",
            border: "none",
            padding: "12px 0",
            color:
              view === "dashboard"
                ? "var(--primary-color)"
                : "var(--text-secondary)",
            fontWeight: view === "dashboard" ? 700 : 500,
            fontSize: "15px",
            borderBottom:
              view === "dashboard"
                ? "2px solid var(--primary-color)"
                : "2px solid transparent",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Dashboard
        </button>
        <button
          onClick={() => {
            setView("analytics");
            setNewJobStep(1);
            setSelectedCustomer(null);
            setCustomerQuery("");
            setSelectedJobRacket("");
          }}
          style={{
            background: "none",
            border: "none",
            padding: "12px 0",
            color:
              view === "analytics"
                ? "var(--primary-color)"
                : "var(--text-secondary)",
            fontWeight: view === "analytics" ? 700 : 500,
            fontSize: "15px",
            borderBottom:
              view === "analytics"
                ? "2px solid var(--primary-color)"
                : "2px solid transparent",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Analytics
        </button>
        <button
          onClick={() => {
            setView("orders");
            setNewJobStep(1);
            setSelectedCustomer(null);
            setCustomerQuery("");
            setSelectedJobRacket("");
          }}
          style={{
            background: "none",
            border: "none",
            padding: "12px 0",
            color:
              view === "orders"
                ? "var(--primary-color)"
                : "var(--text-secondary)",
            fontWeight: view === "orders" ? 700 : 500,
            fontSize: "15px",
            borderBottom:
              view === "orders"
                ? "2px solid var(--primary-color)"
                : "2px solid transparent",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Ordens
        </button>
        <button
          onClick={() => {
            setView("settings");
            setNewJobStep(1);
            setSelectedCustomer(null);
            setCustomerQuery("");
            setSelectedJobRacket("");
          }}
          style={{
            background: "none",
            border: "none",
            padding: "12px 0",
            color:
              view === "settings"
                ? "var(--primary-color)"
                : "var(--text-secondary)",
            fontWeight: view === "settings" ? 700 : 500,
            fontSize: "15px",
            borderBottom:
              view === "settings"
                ? "2px solid var(--primary-color)"
                : "2px solid transparent",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Configurações
        </button>
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          padding: "0 24px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        {/* Dashboard View */}
        {view === "dashboard" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <h2
                  style={{
                    fontSize: "24px",
                    color: "var(--text-primary)",
                    margin: 0,
                  }}
                >
                  Gestão e Operação
                </h2>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                {currentUser?.role === 'ADMIN' && availablePoints.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginRight: "16px" }}>
                    <span style={{ fontSize: "14px", color: "var(--text-secondary)" }}>Ponto:</span>
                    <select
                      style={{
                        background: "transparent",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-light)",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "14px",
                        outline: "none"
                      }}
                      value={selectedPointFilter}
                      onChange={(e) => setSelectedPointFilter(e.target.value)}
                    >
                      <option value="Todos" style={{ color: "#000" }}>Todos</option>
                      {availablePoints.map(p => <option key={p} value={p} style={{ color: "#000" }}>{p}</option>)}
                    </select>
                  </div>
                )}
                <button
                  onClick={() => {
                    setNewJobStep(1);
                    setSelectedCustomer(null);
                    setCustomerQuery("");
                    setSelectedJobRacket("");
                    setCurrentOrderCode("");
                    setEditingJobId(null);
                    setView("new_job");
                  }}
                  className="button-primary"
                  style={{ padding: "8px 24px", fontSize: "14px" }}
                >
                  <Plus size={18} /> Novo Encordoamento
                </button>
              </div>
            </div>

            {/* Configurable Status Tiles - Row 1 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => {
                  setNewJobStep(1);
                  setSelectedCustomer(null);
                  setCustomerQuery("");
                  setSelectedJobRacket("");
                  setCurrentOrderCode("");
                  setEditingJobId(null);
                  setView("new_job");
                }}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid ${getStatusColor("dropping_off")}`,
                  background: "var(--bg-panel)",
                }}
              >
                <PackageOpen
                  size={24}
                  color={getStatusColor("dropping_off")}
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>
                  Recebimento
                </h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Raquetes chegando
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => setActiveFilter("to_string")}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid ${getStatusColor("to_string")}`,
                  background:
                    activeFilter === "to_string"
                      ? "rgba(255,255,255,0.2)"
                      : "var(--bg-panel)",
                }}
              >
                <Scissors
                  size={24}
                  color={getStatusColor("to_string")}
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>
                  Para Encordoar:{" "}
                  {
                    jobs.filter(
                      (j) => j.type === "to_string" || j.type === "stringing",
                    ).length
                  }
                </h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Fila de trabalho
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => setActiveFilter("picking_up")}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid ${getStatusColor("picking_up")}`,
                  background:
                    activeFilter === "picking_up"
                      ? "rgba(255,255,255,0.2)"
                      : "var(--bg-panel)",
                }}
              >
                <CheckCircle
                  size={24}
                  color={getStatusColor("picking_up")}
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>
                  Para Retirar:{" "}
                  {jobs.filter((j) => j.type === "picking_up").length}
                </h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Prontas
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => setActiveFilter("picked_up")}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid #10B981`,
                  background:
                    activeFilter === "picked_up"
                      ? "rgba(255,255,255,0.2)"
                      : "var(--bg-panel)",
                }}
              >
                <CheckCircle
                  size={24}
                  color="#10B981"
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>
                  Entregues: {jobs.filter((j) => j.type === "picked_up").length}
                </h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Finalizadas
                </p>
              </motion.div>
            </div>

            {/* Configurable Status Tiles - Row 2 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => setActiveFilter("all")}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid var(--text-secondary)`,
                  background:
                    activeFilter === "all"
                      ? "rgba(255,255,255,0.2)"
                      : "var(--bg-panel)",
                }}
              >
                <ClipboardList
                  size={24}
                  color="var(--text-secondary)"
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>
                  Todas: {jobs.length}
                </h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Visão Geral
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => setView("customers")}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid #9B51E0`,
                }}
              >
                <Users
                  size={24}
                  color="#9B51E0"
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>Clientes</h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Base de dados
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-panel"
                onClick={() => setView("professors")}
                style={{
                  padding: "20px",
                  cursor: "pointer",
                  borderLeft: `4px solid #E28743`,
                  background: "var(--bg-panel)",
                }}
              >
                <Users
                  size={24}
                  color="#E28743"
                  style={{ marginBottom: "12px" }}
                />
                <h3 style={{ fontSize: "18px", fontWeight: 700 }}>
                  Professores
                </h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "14px",
                    marginTop: "4px",
                  }}
                >
                  Comissionados
                </p>
              </motion.div>
            </div>

            {/* Calendar / List View Header */}
            <div
              className="glass-panel"
              style={{ padding: "24px", minHeight: "400px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                }}
              >
                <span style={{ fontSize: "20px", fontWeight: 700 }}>
                  {activeFilter === "all"
                    ? "Todos os Pedidos"
                    : activeFilter === "to_string"
                      ? "Fila de Encordoamento"
                      : "Prontos para Retirada"}
                  {dateFilter === "today" && " (Retiradas de Hoje)"}
                </span>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Buscar por nome ou telefone..."
                    value={jobSearchQuery}
                    onChange={(e) => setJobSearchQuery(e.target.value)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.2)",
                      background: "rgba(0,0,0,0.2)",
                      color: "white",
                      outline: "none",
                      width: "250px",
                    }}
                  />
                  <div style={{ display: "flex", gap: "4px" }}>
                    {["all", "today", "week", "month", "year"].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setDateFilter(filter as any)}
                        style={{
                          background:
                            dateFilter === filter
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(255,255,255,0.1)",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "12px",
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        {filter === "all"
                          ? "Todos"
                          : filter === "today"
                            ? "Dia"
                            : filter === "week"
                              ? "Semana"
                              : filter === "month"
                                ? "Mês"
                                : "Ano"}
                      </button>
                    ))}
                  </div>

                  <div
                    style={{ display: "flex", gap: "2px", marginLeft: "12px" }}
                  >
                    <button
                      onClick={() => exportDashboardData("excel")}
                      style={{
                        background: "#6FCF97",
                        border: "none",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "4px 0 0 4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Gerar Excel"
                    >
                      <FileSpreadsheet size={16} />
                    </button>
                    <button
                      onClick={() => exportDashboardData("pdf")}
                      style={{
                        background: "#D93B65",
                        border: "none",
                        color: "white",
                        padding: "6px 12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Gerar PDF"
                    >
                      <FileText size={16} />
                    </button>
                    <button
                      onClick={() => exportDashboardData("csv")}
                      style={{
                        background: "#F2C94C",
                        border: "none",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Gerar CSV"
                    >
                      <FileJson size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Jobs List */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {filteredJobs.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Nenhum evento para exibir
                  </div>
                ) : (
                  paginatedJobs.map((job) => (
                    <div
                      key={job.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          activeFilter === "to_string" ||
                          activeFilter === "picking_up" ||
                          activeFilter === "picked_up" ||
                          activeFilter === "all"
                            ? "minmax(120px, 1fr) 2fr 1fr 1fr auto"
                            : "1fr 2fr 1fr 1fr",
                        alignItems: "center",
                        padding: "16px",
                        background: "rgba(0,0,0,0.2)",
                        borderRadius: "16px",
                        gap: "16px",
                        borderLeft: `4px solid ${getStatusColor(job.type)}`,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{job.date}</div>
                        {job.pickupDate && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#F2C94C",
                              fontWeight: 700,
                            }}
                          >
                            Retirada:{" "}
                            {new Date(job.pickupDate).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "16px" }}>
                          {job.customerName}
                          {(() => {
                            const c = customers.find(
                              (c: any) => c.name === job.customerName,
                            );
                            return c?.numericId ? (
                              <span
                                style={{
                                  fontSize: "13px",
                                  color: "var(--text-secondary)",
                                  marginLeft: "6px",
                                }}
                              >
                                (ID: {c.numericId})
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <div
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: "13px",
                          }}
                        >
                          {job.racketModel}
                          {(() => {
                            const r = rackets?.find(
                              (r: any) => r.id === job.racketId,
                            );
                            return r?.identifier ? ` [${r.identifier}]` : "";
                          })()}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "var(--primary-color)",
                          }}
                        >
                          {job.tension}
                        </div>
                      </div>

                      {activeFilter === "to_string" ? (
                        <>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              Mains: {job.stringMains || "N/A"}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              Cross:{" "}
                              {job.stringCross || job.stringMains || "N/A"}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Excluir este serviço na Nuvem?",
                                  )
                                ) {
                                  fetch(`${API_URL}/api/jobs/${job.id}`, {
                                    method: "DELETE",
                                    headers: getAuthHeader(),
                                  })
                                    .then(async (res) => {
                                      if (!res.ok) {
                                        const err = await res
                                          .json()
                                          .catch(() => ({}));
                                        throw new Error(
                                          err.error ||
                                            "Erro ao deletar no servidor",
                                        );
                                      }
                                      if (import.meta.env.VITE_BFF_URL) {
                                        const remainingJobs = jobs.filter(
                                          (j: any) =>
                                            j.orderCode === job.orderCode &&
                                            j.id !== job.id,
                                        );
                                        if (remainingJobs.length === 0) {
                                          fetch(
                                            `${import.meta.env.VITE_BFF_URL}/api/pedidos/${job.orderCode}`,
                                            { method: "DELETE" },
                                          ).catch(console.error);
                                        } else {
                                          const orderItems = remainingJobs.flatMap((j: any) => getJobOrderItems(j, appSettings));
                                          const orderTotal =
                                            remainingJobs.reduce(
                                              (acc: number, j: any) =>
                                                acc + j.price,
                                              0,
                                            );
                                          const customer = customers.find((c: any) => c.id === (remainingJobs[0].customerId || job.customerId));

                                          fetch(
                                            `${import.meta.env.VITE_BFF_URL}/api/pedidos/${job.orderCode}`,
                                            {
                                              method: "PUT",
                                              headers: {
                                                "Content-Type":
                                                  "application/json",
                                              },
                                              body: JSON.stringify({
                                                id: job.orderCode,
                                                customerId: remainingJobs[0].customerId || job.customerId,
                                                customerName: customer?.name,
                                                customerEmail: customer?.email,
                                                customerPhone: customer?.phone,
                                                customerDocument: customer?.cpfCnpj || customer?.document,
                                                customerAddress: customer?.address,
                                                customerAddressNumber: customer?.addressNumber,
                                                customerComplement: customer?.complement,
                                                customerNeighborhood: customer?.neighborhood,
                                                customerCity: customer?.city,
                                                customerState: customer?.state,
                                                customerZipCode: customer?.cep,
                                                total: orderTotal,
                                                items: orderItems,
                                              }),
                                            },
                                          ).catch(console.error);
                                        }
                                      }
                                      setJobs((prev) =>
                                        prev.filter((j) => j.id !== job.id),
                                      );
                                      fetchJobs();
                                    })
                                    .catch((err) => {
                                      console.error(err);
                                      alert(
                                        "Falha ao excluir o serviço. Verifique a conexão.",
                                      );
                                    });
                                }
                              }}
                              style={{
                                background: "#E04A59",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                            <button
                              onClick={() => {
                                import("../utils/printUtils").then((m) =>
                                  m.printLabel(
                                    job,
                                    "full",
                                    rackets?.find(
                                      (r: any) => r.id === job.racketId,
                                    ),
                                  ),
                                );
                              }}
                              style={{
                                background: "#9B51E0",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Imprimir Etiqueta"
                            >
                              <Printer size={16} />
                            </button>
                            <button
                              onClick={() => {
                                const cust = customers.find(
                                  (c) => c.name === job.customerName,
                                );
                                startEditingJob(job, cust);
                              }}
                              style={{
                                background: "#4298E7",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Editar Recebimento"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setActiveOrderJob(job);
                                setView("order_details");
                              }}
                              style={{
                                background: "#C25488",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Ordem"
                            >
                              <ClipboardList size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setActiveStringingJob(job);
                                setDashboardStringer(job.stringerName || "");
                                setView("stringing");
                                setJobs((prev) =>
                                  prev.map((j) =>
                                    j.id === job.id
                                      ? { ...j, type: "stringing" }
                                      : j,
                                  ),
                                );
                              }}
                              style={{
                                background: "#F2C94C",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "var(--text-dark)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Encordoar"
                            >
                              <Grid size={16} />
                            </button>
                          </div>
                        </>
                      ) : activeFilter === "picking_up" ? (
                        <>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 600, fontSize: "16px" }}>
                              BRL{" "}
                              {job.price != null && job.price !== ""
                                ? Number(job.price).toFixed(2)
                                : "120.00"}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: job.paid ? "#6FCF97" : "#E04A59",
                                fontWeight: 600,
                              }}
                            >
                              {job.paid ? "PAGO" : "NÃO PAGO"}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                import("../utils/printUtils").then((m) =>
                                  m.printLabel(
                                    job,
                                    "heart",
                                    rackets?.find(
                                      (r: any) => r.id === job.racketId,
                                    ),
                                  ),
                                );
                              }}
                              style={{
                                background: "#E5E7EB",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "#6B7280",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Imprimir Etiqueta (Coração)"
                            >
                              <Ticket size={16} strokeWidth={3} />
                            </button>
                            <button
                              onClick={() => {
                                const cust = customers.find(
                                  (c) => c.name === job.customerName,
                                );
                                if (!cust || !cust.phone)
                                  return alert(
                                    "Telefone do cliente não encontrado!",
                                  );
                                const msg = `Olá ${job.customerName}, a sua raquete (${job.racketModel}) já foi encordoada e está pronta para retirada na TechTennis!`;
                                window.open(
                                  `https://wa.me/${cust.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(msg)}`,
                                  "_blank",
                                );
                              }}
                              style={{
                                background: "#25D366",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Avisar Cliente no WhatsApp"
                            >
                              <MessageCircle size={16} />
                            </button>
                            <button
                              onClick={() => handleNotifyPickup(job.id, 'cliente')}
                              style={{
                                background: "#4F46E5",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Avisar Cliente via App TT"
                            >
                              <Bell size={16} />
                            </button>
                            {job.commissionedProfessorId && (
                              <>
                                <button
                                  onClick={() => {
                                    const prof = professors.find(
                                      (p) => p.id === job.commissionedProfessorId,
                                    );
                                    if (!prof || !prof.phone)
                                      return alert(
                                        "Telefone do professor não encontrado!",
                                      );
                                    const msg = `Olá Professor, a raquete do seu aluno ${job.customerName} (${job.racketModel}) já está pronta para retirada na TechTennis!`;
                                    window.open(
                                      `https://wa.me/${prof.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(msg)}`,
                                      "_blank",
                                    );
                                  }}
                                  style={{
                                    background: "#128C7E",
                                    border: "none",
                                    padding: "8px",
                                    borderRadius: "8px",
                                    color: "white",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title="Avisar Professor no WhatsApp"
                                >
                                  <MessageCircle size={16} />{" "}
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      marginLeft: "4px",
                                    }}
                                  >
                                    Prof
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleNotifyPickup(job.id, 'professor')}
                                  style={{
                                    background: "#4338CA",
                                    border: "none",
                                    padding: "8px",
                                    borderRadius: "8px",
                                    color: "white",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginLeft: "4px"
                                  }}
                                  title="Avisar Professor via App TT"
                                >
                                  <Bell size={16} />{" "}
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      marginLeft: "4px",
                                    }}
                                  >
                                    Prof
                                  </span>
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                const cust = customers.find(
                                  (c) => c.name === job.customerName,
                                );
                                startEditingJob(job, cust);
                              }}
                              style={{
                                background: "#4298E7",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Editar Recebimento"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setActiveOrderJob(job);
                                setView("order_details");
                              }}
                              style={{
                                background: "#C25488",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Ordem"
                            >
                              <ClipboardList size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setActivePaymentJob(job);
                                setIsPaymentModalOpen(true);
                              }}
                              style={{
                                background: job.paid ? "#6FCF97" : "#E04A59",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Pagamento"
                            >
                              <DollarSign
                                size={16}
                                color={job.paid ? "var(--text-dark)" : "white"}
                              />
                            </button>
                            <button
                              onClick={() => {
                                setActivePickupJob(job);
                                setIsPickupModalOpen(true);
                              }}
                              style={{
                                background: "#1A202C",
                                border: "1px solid rgba(255,255,255,0.1)",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Entregar e Finalizar"
                            >
                              <Truck size={16} />
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 600, fontSize: "16px" }}>
                              BRL{" "}
                              {job.price != null && job.price !== ""
                                ? Number(job.price).toFixed(2)
                                : "120.00"}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: job.paid ? "#6FCF97" : "#E04A59",
                                fontWeight: 600,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: "2px",
                              }}
                            >
                              <span>{job.paid ? "PAGO" : "NÃO PAGO"}</span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                {job.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                setActiveOrderJob(job);
                                setView("order_details");
                              }}
                              style={{
                                background: "#C25488",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Ordem"
                            >
                              <ClipboardList size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setActivePaymentJob(job);
                                setIsPaymentModalOpen(true);
                              }}
                              style={{
                                background: job.paid ? "#6FCF97" : "#E04A59",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Pagamento"
                            >
                              <DollarSign
                                size={16}
                                color={job.paid ? "var(--text-dark)" : "white"}
                              />
                            </button>
                            <button
                              onClick={() => {
                                setActiveFeedbackJob(job);
                                setIsFeedbackModalOpen(true);
                              }}
                              style={{
                                background: job.feedback
                                  ? "#8B5CF6"
                                  : "rgba(255, 255, 255, 0.1)",
                                border: job.feedback
                                  ? "none"
                                  : "1px solid rgba(255, 255, 255, 0.2)",
                                padding: "8px",
                                borderRadius: "8px",
                                color: job.feedback
                                  ? "white"
                                  : "rgba(255, 255, 255, 0.4)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title={
                                job.feedback ? "Ver Feedback" : "Sem Feedback"
                              }
                            >
                              <Smile size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}

                {totalJobPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "20px",
                    }}
                  >
                    <div
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Mostrando {(jobCurrentPage - 1) * jobsPerPage + 1} -{" "}
                      {Math.min(
                        jobCurrentPage * jobsPerPage,
                        totalFilteredJobs,
                      )}{" "}
                      de {totalFilteredJobs}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() =>
                          setJobCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={jobCurrentPage === 1}
                        style={{
                          background: "var(--bg-panel)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "white",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          cursor:
                            jobCurrentPage === 1 ? "not-allowed" : "pointer",
                          opacity: jobCurrentPage === 1 ? 0.5 : 1,
                        }}
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() =>
                          setJobCurrentPage((p) =>
                            Math.min(totalJobPages, p + 1),
                          )
                        }
                        disabled={jobCurrentPage === totalJobPages}
                        style={{
                          background: "var(--bg-panel)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "white",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          cursor:
                            jobCurrentPage === totalJobPages
                              ? "not-allowed"
                              : "pointer",
                          opacity: jobCurrentPage === totalJobPages ? 0.5 : 1,
                        }}
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* New Job Flow */}
        {view === "new_job" && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            className="glass-panel"
            style={{ padding: "32px", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "32px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                {newJobStep === 2 && (
                  <button
                    onClick={() => setNewJobStep(1)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--primary-color)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArrowLeft size={24} />
                  </button>
                )}
                <h2 style={{ fontSize: "28px", margin: 0 }}>
                  {newJobStep === 1
                    ? "Recebimento"
                    : "Detalhes do Encordoamento"}
                </h2>
              </div>
              {newJobStep === 2 && selectedCustomer && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    background: "rgba(255,255,255,0.05)",
                    padding: "8px 16px",
                    borderRadius: "100px",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "var(--primary-color)",
                      color: "var(--text-dark)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedCustomer.name.charAt(0).toUpperCase()}
                  </div>
                  <span style={{ fontWeight: 600 }}>
                    {selectedCustomer.name}
                  </span>
                </div>
              )}
            </div>

            {newJobStep === 1 ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!selectedCustomer) {
                    setCustomerError(true);
                    return;
                  }
                  if (commissionedProfessorId && !commissionAction) {
                    alert("Por favor, selecione a regra da comissão (Professor Coletou ou Atualização de Set).");
                    return;
                  }
                  if (!commissionedProfessorId) {
                    setCommissionAction("");
                  }
                  setCustomerError(false);
                  setNewJobStep(2);
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: "1 1 300px", position: "relative" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Cliente
                    </label>
                    <input
                      type="text"
                      placeholder="Insira o nome do cliente"
                      value={
                        selectedCustomer ? selectedCustomer.name : customerQuery
                      }
                      onChange={(e) => {
                        setCustomerQuery(e.target.value);
                        setSelectedCustomer(null);
                        setShowCustomerDropdown(true);
                        setCustomerError(false);
                      }}
                      onClick={() => setShowCustomerDropdown((prev) => !prev)}
                      required
                      style={{
                        ...inputStyle,
                        borderColor: customerError
                          ? "#D93B65"
                          : "var(--border-light)",
                        cursor: "pointer",
                      }}
                    />
                    {customerError && (
                      <span
                        style={{
                          color: "#D93B65",
                          fontSize: "13px",
                          marginTop: "6px",
                          display: "block",
                        }}
                      >
                        Por favor, selecione um cliente cadastrado da lista.
                      </span>
                    )}

                    {/* Autocomplete Dropdown */}
                    <AnimatePresence>
                      {showCustomerDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#2D1E4B",
                            zIndex: 50,
                          borderRadius: "12px",
                            marginTop: "8px",
                            maxHeight: "200px",
                            overflowY: "auto",
                            border: "1px solid var(--border-light)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                          }}
                        >
                          {customers
                            .filter((c: any) => {
                              const queryTokens = customerQuery.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().split(/\s+/);
                              const nameNorm = (c.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                              const matchName = queryTokens.every(token => nameNorm.includes(token));
                              const matchId = c.numericId && c.numericId.toString().startsWith(customerQuery.trim());
                              return matchName || matchId;
                            })
                            .map((customer: any) => (
                              <div
                                key={customer.id}
                                onClick={() => {
                                  setSelectedCustomer(customer);
                                  setCustomerQuery(customer.name);
                                  setShowCustomerDropdown(false);
                                }}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.05)",
                                  color: "white",
                                  fontWeight: 500,
                                }}
                              >
                                {customer.name}{" "}
                                {customer.numericId
                                  ? `(ID: ${customer.numericId})`
                                  : ""}
                              </div>
                            ))}

                          {customers.filter((c: any) => {
                            const queryTokens = customerQuery.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().split(/\s+/);
                            const nameNorm = (c.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                            const matchName = queryTokens.every(token => nameNorm.includes(token));
                            const matchId = c.numericId && c.numericId.toString().startsWith(customerQuery.trim());
                            return matchName || matchId;
                          }).length === 0 && (
                            <div
                              style={{
                                padding: "12px 16px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              Nenhum cliente encontrado
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomerDropdown(true)}
                    style={{
                      height: "50px",
                      padding: "0 24px",
                      borderRadius: "12px",
                      border: "none",
                      background: "rgba(255,255,255,0.1)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                      marginTop: "29px",
                    }}
                  >
                    <Search size={18} /> Buscar Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setIsCustomerModalOpen(true);
                    }}
                    className="button-primary"
                    style={{
                      height: "50px",
                      padding: "0 24px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "29px",
                    }}
                  >
                    <Plus size={18} /> Novo Cliente
                  </button>
                </div>

                {/* Professor Comissionado Row */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: "1 1 300px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Professor Comissionado
                    </label>
                    <select
                      value={commissionedProfessorId}
                      onChange={(e) =>
                        setCommissionedProfessorId(e.target.value)
                      }
                      style={{ ...inputStyle, width: "100%" }}
                    >
                      <option value="">N/A (Nenhum)</option>
                      {professors.map((prof) => (
                        <option key={prof.id} value={prof.id}>
                          {prof.name}
                        </option>
                      ))}
                    </select>
                    {commissionedProfessorId && (
                      <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexDirection: 'column' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', cursor: 'pointer', opacity: commissionAction === 'COLETA' ? 1 : 0.6 }}>
                          <input 
                            type="radio" 
                            name="commissionAction" 
                            value="COLETA" 
                            checked={commissionAction === 'COLETA'} 
                            onChange={() => setCommissionAction('COLETA')}
                            style={{ accentColor: 'var(--primary-color)', width: '18px', height: '18px' }}
                          />
                          Professor Coletou
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', cursor: 'pointer', opacity: commissionAction === 'ATUALIZACAO_SET' ? 1 : 0.6 }}>
                          <input 
                            type="radio" 
                            name="commissionAction" 
                            value="ATUALIZACAO_SET" 
                            checked={commissionAction === 'ATUALIZACAO_SET'} 
                            onChange={() => setCommissionAction('ATUALIZACAO_SET')}
                            style={{ accentColor: 'var(--primary-color)', width: '18px', height: '18px' }}
                          />
                          Atualização de Set
                        </label>
                      </div>
                    )}
                  </div>
                  <div style={{ flex: "1 1 300px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Encordoador
                    </label>
                    <select
                      value={dashboardStringer}
                      onChange={(e) => setDashboardStringer(e.target.value)}
                      style={{ ...inputStyle, width: "100%" }}
                    >
                      <option value="">N/A (Não selecionado)</option>
                      {[...(appSettings.stringers || [])]
                        .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                        .map((s: any) => {
                          const val = typeof s === 'string' ? s : s.name;
                          return (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "24px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Previsão de Entrega (Order pick up)
                    </label>
                    <input
                      type="datetime-local"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      min={new Date(
                        Date.now() - new Date().getTimezoneOffset() * 60000,
                      )
                        .toISOString()
                        .slice(0, 16)}
                      required
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Ponto de Encordoamento
                    </label>
                    <select
                      value={jobStringingPoint}
                      onChange={(e) => setJobStringingPoint(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Selecione...</option>
                      {(() => {
                        const opts = [...availablePointsForUser];
                        if (jobStringingPoint && !opts.includes(jobStringingPoint)) {
                          opts.unshift(jobStringingPoint);
                        }
                        return opts.map((p: string) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Observações
                  </label>
                  <textarea
                    rows={4}
                    value={pickupNotes}
                    onChange={(e) => setPickupNotes(e.target.value)}
                    style={{ ...inputStyle, resize: "none" }}
                  ></textarea>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "16px",
                    gap: "16px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setView("dashboard")}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "none",
                      padding: "16px 40px",
                      borderRadius: "24px",
                      fontWeight: 700,
                      fontSize: "16px",
                      cursor: "pointer",
                    }}
                  >
                    Fechar
                  </button>
                  <button
                    type="submit"
                    className="button-primary"
                    style={{ padding: "16px 40px", fontSize: "16px" }}
                  >
                    Continuar
                  </button>
                </div>
              </form>
            ) : (
              <form
                id="newJobForm"
                onSubmit={handleSave}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {/* Racket Selection Row */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    paddingBottom: "24px",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div style={{ flex: "1 1 300px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Raquete
                    </label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <select
                        required
                        style={{ ...inputStyle, flex: 1 }}
                        value={selectedJobRacket}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedJobRacket(val);
                          if (val) {
                            const pastJob = jobs.find(
                              (j) =>
                                j.racketId === val &&
                                (!!j.stringMains || !!j.mainString),
                            );
                            if (pastJob) {
                              setStringingType(pastJob.stringingType || "ATW");
                              setTensionUnit(pastJob.tensionUnit || "Lbs");
                              setMainString(
                                pastJob.stringMains || pastJob.mainString || "",
                              );
                              setCrossString(
                                pastJob.stringCross ||
                                  pastJob.crossString ||
                                  "",
                              );

                              const match =
                                pastJob.tension?.match(/(\d+)(?:\/(\d+))?/);
                              setTensionMain(
                                pastJob.tensionMain ||
                                  (match ? parseInt(match[1]) : ""),
                              );
                              setTensionCross(
                                pastJob.tensionCross ||
                                  (match
                                    ? match[2]
                                      ? parseInt(match[2])
                                      : parseInt(match[1])
                                    : ""),
                              );
                              setIsHybrid(
                                pastJob.isHybrid !== undefined
                                  ? pastJob.isHybrid
                                  : match && match[2]
                                    ? true
                                    : false,
                              );

                              setPreStretchMain(pastJob.preStretchMain || "");
                              setPreStretchCross(pastJob.preStretchCross || "");
                              setIsStringing(true);
                            } else {
                              setStringingType("Não definido");
                              setTensionUnit("Lbs");
                              setMainString("");
                              setCrossString("");
                              setTensionMain("");
                              setTensionCross("");
                              setIsHybrid(false);
                              setIsStringing(true);
                              setPreStretchMain("");
                              setPreStretchCross("");
                            }
                          } else {
                            setStringingType("Não definido");
                            setTensionUnit("Lbs");
                            setMainString("");
                            setCrossString("");
                            setTensionMain("");
                            setTensionCross("");
                            setIsHybrid(false);
                            setIsStringing(true);
                            setPreStretchMain("");
                            setPreStretchCross("");
                          }
                        }}
                      >
                        <option value="">
                          Selecione a raquete do cliente...
                        </option>
                        {(() => {
                          const customerRackets = rackets.filter(
                            (r) => r.customerId === selectedCustomer?.id,
                          );
                          const usedRacketIdsInOrder = jobs
                            .filter(
                              (j) =>
                                j.orderCode === currentOrderCode &&
                                j.id !== editingJobId,
                            )
                            .map((j) => j.racketId);

                          customerRackets.sort((a, b) => {
                            const nameA = a.identifier
                              ? `${a.name.trim()} [${a.identifier}]`
                              : a.name.trim();
                            const nameB = b.identifier
                              ? `${b.name.trim()} [${b.identifier}]`
                              : b.name.trim();
                            return nameA.localeCompare(nameB, undefined, {
                              numeric: true,
                              sensitivity: "base",
                            });
                          });

                          return customerRackets.map((r) => {
                            const suffix = r.identifier
                              ? ` [${r.identifier}]`
                              : "";
                            const displayName = `${r.name.trim()}${suffix}`;

                            const isUsed = usedRacketIdsInOrder.includes(r.id);
                            return (
                              <option
                                key={r.id}
                                value={r.id}
                                disabled={isUsed}
                                style={{ color: isUsed ? "#888" : undefined }}
                              >
                                {displayName} {isUsed ? "(Já na ordem)" : ""}
                              </option>
                            );
                          });
                        })()}
                      </select>
                      {selectedJobRacket && (
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            type="button"
                            onClick={() => {
                              const racketToEdit = rackets.find(
                                (r) => r.id === selectedJobRacket,
                              );
                              if (racketToEdit) {
                                setRacketFormDefault(racketToEdit);
                                setIsRacketModalOpen(true);
                              }
                            }}
                            style={{
                              padding: "0 16px",
                              borderRadius: "12px",
                              border: "none",
                              background: "#3A52EE",
                              color: "white",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              transition: "background 0.2s",
                            }}
                            title="Editar Raquete"
                          >
                            <Edit size={20} />
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (
                                window.confirm(
                                  "Tem certeza que deseja apagar esta raquete do cadastro do cliente?",
                                )
                              ) {
                                try {
                                  await fetch(
                                    `${API_URL}/api/rackets/${selectedJobRacket}`,
                                    {
                                      method: "DELETE",
                                      headers: getAuthHeader(),
                                    },
                                  );
                                  fetchRackets();
                                  setSelectedJobRacket("");
                                } catch (e) {
                                  console.error(e);
                                }
                              }
                            }}
                            style={{
                              padding: "0 16px",
                              borderRadius: "12px",
                              border: "none",
                              background: "#EB5757",
                              color: "white",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              transition: "background 0.2s",
                            }}
                            title="Excluir Raquete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCloneRacketModalOpen(true)}
                    style={{
                      height: "50px",
                      padding: "0 24px",
                      borderRadius: "12px",
                      border: "none",
                      background: "rgba(255,255,255,0.1)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    <Search size={18} /> Buscar Raquete
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRacketFormDefault(null);
                      setIsRacketModalOpen(true);
                    }}
                    className="button-primary"
                    style={{
                      height: "50px",
                      padding: "0 24px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Plus size={18} /> Nova Raquete
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCloneRacketModalOpen(true)}
                    style={{
                      height: "50px",
                      padding: "0 24px",
                      borderRadius: "12px",
                      border: "none",
                      background: "rgba(255,255,255,0.1)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    <Copy size={18} /> Clonar Raquete
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(250px, 1fr) 3fr",
                    gap: "32px",
                  }}
                >
                  {/* Left Column Configs */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Encordoamento
                      </label>
                      <select
                        style={inputStyle}
                        value={isStringing ? "Sim" : "Nao"}
                        onChange={(e) =>
                          setIsStringing(e.target.value === "Sim")
                        }
                      >
                        <option value="Sim">Sim</option>
                        <option value="Nao">Não</option>
                      </select>
                    </div>

                    <div
                      onClick={() => setIsHistoryModalOpen(true)}
                      style={{
                        padding: "16px",
                        background: "var(--primary-color)",
                        borderRadius: "12px",
                        color: "var(--text-dark)",
                        fontWeight: 700,
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "transform 0.1s",
                      }}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.transform = "scale(0.98)")
                      }
                      onMouseUp={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      <Search size={18} /> Histórico Recente
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "16px",
                        background: "rgba(255,255,255,0.05)",
                        borderRadius: "12px",
                      }}
                    >
                      <span>Usa rolo próprio</span>
                      <input
                        type="checkbox"
                        checked={hasOwnReel}
                        onChange={(e) => setHasOwnReel(e.target.checked)}
                        style={{
                          accentColor: "#D93B65",
                          width: "20px",
                          height: "20px",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "16px",
                        background: "rgba(255,255,255,0.05)",
                        borderRadius: "12px",
                      }}
                    >
                      <span>Usa set próprio</span>
                      <input
                        type="checkbox"
                        checked={hasOwnSet}
                        onChange={(e) => setHasOwnSet(e.target.checked)}
                        style={{
                          accentColor: "#D93B65",
                          width: "20px",
                          height: "20px",
                        }}
                      />
                    </div>

                    {auxServices.map((svc, idx) => (
                      <div key={svc.type}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {svc.type}
                        </label>
                        <select
                          value={svc.isActive ? "Sim" : "Não"}
                          onChange={(e) => {
                            const newSvc = [...auxServices];
                            newSvc[idx].isActive = e.target.value === "Sim";
                            setAuxServices(newSvc);
                          }}
                          style={inputStyle}
                        >
                          <option value="Não">Não</option>
                          <option value="Sim">Sim</option>
                        </select>
                        {svc.isActive && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              <div
                                style={{
                                  flex: "1 1 120px",
                                  position: "relative",
                                }}
                              >
                                <span
                                  style={{
                                    position: "absolute",
                                    left: "16px",
                                    top: "14px",
                                    color: "var(--text-secondary)",
                                    fontWeight: 600,
                                  }}
                                >
                                  BRL
                                </span>
                                <input
                                  type="number"
                                  placeholder="Valor"
                                  value={svc.price || ""}
                                  onChange={(e) => {
                                    const newSvc = [...auxServices];
                                    newSvc[idx].price = Number(e.target.value);
                                    setAuxServices(newSvc);
                                  }}
                                  style={{ ...inputStyle, paddingLeft: "56px" }}
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  flex: "2 1 200px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: "100px",
                                    position: "relative",
                                  }}
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: "12px",
                                      top: "14px",
                                      color: "var(--text-secondary)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    BRL
                                  </span>
                                  <input
                                    type="number"
                                    placeholder="Desc"
                                    value={svc.discountValue || ""}
                                    onChange={(e) => {
                                      const newSvc = [...auxServices];
                                      newSvc[idx].discountValue =
                                        e.target.value === ""
                                          ? ""
                                          : Number(e.target.value);
                                      setAuxServices(newSvc);
                                    }}
                                    style={{
                                      ...inputStyle,
                                      paddingLeft: "48px",
                                    }}
                                    title="Desconto BRL"
                                  />
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: "80px",
                                    position: "relative",
                                  }}
                                >
                                  <input
                                    type="number"
                                    placeholder="%"
                                    value={svc.discountPercent || ""}
                                    onChange={(e) => {
                                      const newSvc = [...auxServices];
                                      newSvc[idx].discountPercent =
                                        e.target.value === ""
                                          ? ""
                                          : Number(e.target.value);
                                      setAuxServices(newSvc);
                                    }}
                                    style={{
                                      ...inputStyle,
                                      paddingRight: "28px",
                                    }}
                                    title="Desconto %"
                                  />
                                  <span
                                    style={{
                                      position: "absolute",
                                      right: "12px",
                                      top: "14px",
                                      color: "var(--text-secondary)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    %
                                  </span>
                                </div>
                              </div>
                            </div>
                            <input
                              type="text"
                              placeholder="Observações"
                              value={svc.notes}
                              onChange={(e) => {
                                const newSvc = [...auxServices];
                                newSvc[idx].notes = e.target.value;
                                setAuxServices(newSvc);
                              }}
                              style={inputStyle}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Right Column / Main Inputs */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Tipo de Encordoamento
                        </label>
                        <select
                          disabled={!isStringing}
                          value={stringingType}
                          onChange={(e) => setStringingType(e.target.value)}
                          style={inputStyle}
                        >
                          <option>Não definido</option>
                          <option>2 nós</option>
                          <option>4 nós</option>
                          <option>ATW</option>
                        </select>
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Unidade de Tensão
                        </label>
                        <select
                          disabled={!isStringing}
                          value={tensionUnit}
                          onChange={(e) => setTensionUnit(e.target.value)}
                          style={inputStyle}
                        >
                          <option>Lbs</option>
                          <option>Kg</option>
                        </select>
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Preço Base (BRL) - Final c/ desconto abatido
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              ...inputStyle,
                              flex: "1 1 120px",
                              background: "rgba(0,0,0,0.2)",
                              color: "white",
                              cursor: "not-allowed",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Preço Automático (Baseado na tabela de cordas)"
                          >
                            <span>{price || "0.00"}</span>
                          </div>
                          <div
                            style={{
                              flex: "2 1 200px",
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                minWidth: "100px",
                                position: "relative",
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  left: "12px",
                                  top: "14px",
                                  color: "var(--text-secondary)",
                                  fontWeight: 600,
                                }}
                              >
                                BRL
                              </span>
                              <input
                                type="number"
                                placeholder="Desc"
                                value={priceDiscountValue || ""}
                                onChange={(e) =>
                                  setPriceDiscountValue(
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                  )
                                }
                                style={{ ...inputStyle, paddingLeft: "50px" }}
                                title="Desconto Base (BRL)"
                              />
                            </div>
                            <div
                              style={{
                                flex: 1,
                                minWidth: "80px",
                                position: "relative",
                              }}
                            >
                              <input
                                type="number"
                                placeholder="%"
                                value={priceDiscountPercent || ""}
                                onChange={(e) =>
                                  setPriceDiscountPercent(
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                  )
                                }
                                style={{ ...inputStyle, paddingRight: "28px" }}
                                title="Desconto Base (%)"
                              />
                              <span
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "14px",
                                  color: "var(--text-secondary)",
                                  fontWeight: 600,
                                }}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        </div>
                        {(Number(price) > 0 ||
                          auxServices.some((s) => s.isActive)) && (
                          <div
                            style={{
                              marginTop: "12px",
                              fontSize: "14px",
                              color: "#6FCF97",
                              fontWeight: 700,
                            }}
                          >
                            Total com descontos e extras aplicados: BRL{" "}
                            {displayFinalPrice}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Corda Main
                        </label>
                        <input
                          disabled={!isStringing}
                          required={isStringing}
                          value={mainString}
                          onChange={(e) => setMainString(e.target.value)}
                          style={inputStyle}
                          list="main-string-list"
                          placeholder="Selecione ou digite..."
                          autoComplete="off"
                        />
                        <datalist id="main-string-list">
                          {[...(appSettings.strings || [])]
                            .sort((a: any, b: any) => {
                              const A = typeof a === "string" ? a : a.name;
                              const B = typeof b === "string" ? b : b.name;
                              return String(A).localeCompare(String(B), undefined, { sensitivity: 'base' });
                            })
                            .map((s: any) => {
                              const name = typeof s === "string" ? s : s.name;
                              return <option key={name} value={name} />;
                            })}
                        </datalist>
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Tensão Main (Kg/Lbs)
                        </label>
                        <input
                          disabled={!isStringing}
                          type="number"
                          placeholder="Tensão Main"
                          required={isStringing}
                          value={tensionMain}
                          onChange={(e) =>
                            setTensionMain(
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Pre-stretch Main (%)
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: 20%"
                          disabled={!isStringing}
                          value={preStretchMain}
                          onChange={(e) => setPreStretchMain(e.target.value)}
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <AnimatePresence>
                      {isHybrid && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr 1fr",
                            gap: "16px",
                            overflow: "hidden",
                          }}
                        >
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              Corda Cross
                            </label>
                            <input
                              disabled={!isStringing}
                              required={isStringing}
                              value={crossString}
                              onChange={(e) => setCrossString(e.target.value)}
                              style={inputStyle}
                              list="cross-string-list"
                              placeholder="Selecione ou digite..."
                              autoComplete="off"
                            />
                            <datalist id="cross-string-list">
                              {[...(appSettings.strings || [])]
                                .sort((a: any, b: any) => {
                                  const A = typeof a === "string" ? a : a.name;
                                  const B = typeof b === "string" ? b : b.name;
                                  return String(A).localeCompare(String(B), undefined, { sensitivity: 'base' });
                                })
                                .map((s: any) => {
                                  const name =
                                    typeof s === "string" ? s : s.name;
                                  return <option key={name} value={name} />;
                                })}
                            </datalist>
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              Tensão Cross (Kg/Lbs)
                            </label>
                            <input
                              disabled={!isStringing}
                              type="number"
                              placeholder="Tensão Cross"
                              required={isStringing}
                              value={tensionCross}
                              onChange={(e) =>
                                setTensionCross(
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value),
                                )
                              }
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              Pre-stretch Cross (%)
                            </label>
                            <input
                              disabled={!isStringing}
                              type="text"
                              placeholder="Ex: 20%"
                              value={preStretchCross}
                              onChange={(e) =>
                                setPreStretchCross(e.target.value)
                              }
                              style={inputStyle}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {isHybrid ? (
                      <div
                        onClick={() => setIsHybrid(false)}
                        style={{
                          background: "#F9D0DA",
                          padding: "16px",
                          borderRadius: "12px",
                          textAlign: "center",
                          cursor: "pointer",
                          fontWeight: 600,
                          color: "#D93B65",
                          transition: "background 0.2s",
                        }}
                      >
                        Cross igual às Mains
                      </div>
                    ) : (
                      <div
                        onClick={() => setIsHybrid(true)}
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          padding: "16px",
                          borderRadius: "12px",
                          textAlign: "center",
                          cursor: "pointer",
                          fontWeight: 600,
                          color: "var(--text-secondary)",
                          transition: "background 0.2s",
                        }}
                      >
                        Diferenciar cordas Cross (Híbrido)
                      </div>
                    )}

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 2fr 2fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Logo
                        </label>
                        <select
                          value={hasLogo ? "Sim" : "Não"}
                          onChange={(e) => setHasLogo(e.target.value === "Sim")}
                          style={inputStyle}
                        >
                          <option value="Não">Não</option>
                          <option value="Sim">Sim</option>
                        </select>
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Notas do Logo
                        </label>
                        <textarea
                          rows={2}
                          value={logoNotes}
                          onChange={(e) => setLogoNotes(e.target.value)}
                          style={{ ...inputStyle, resize: "none" }}
                        ></textarea>
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Notas sobre a raquete
                        </label>
                        <textarea
                          rows={2}
                          value={racketNotes}
                          onChange={(e) => setRacketNotes(e.target.value)}
                          style={{ ...inputStyle, resize: "none" }}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                    marginTop: "32px",
                    paddingTop: "24px",
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setView("dashboard");
                      setNewJobStep(1);
                      setCurrentOrderCode("");
                    }}
                    style={{
                      padding: "16px 32px",
                      background: "rgba(255,255,255,0.1)",
                      border: "none",
                      borderRadius: "100px",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Fechar
                  </button>
                  <button
                    disabled={isSavingMultiple}
                    type="button"
                    onClick={async () => {
                      const f = document.getElementById(
                        "newJobForm",
                      ) as HTMLFormElement;
                      if (f && f.reportValidity()) {
                        const newCode =
                          currentOrderCode ||
                          generateUniqueAlphanumericCode(jobs);
                        if (!currentOrderCode) setCurrentOrderCode(newCode);

                        const finalPriceAux = finalBasePrice + finalAuxPrice;
                        const newJob = {
                          id: Date.now().toString(),
                          orderCode: newCode,
                          customerId: selectedCustomer
                            ? selectedCustomer.id
                            : undefined,
                          customerName: selectedCustomer
                            ? selectedCustomer.name
                            : "Desconhecido",
                          racketModel:
                            rackets.find((r) => r.id === selectedJobRacket)
                              ?.name || "Raquete Customizada",
                          date: new Date().toLocaleDateString("pt-BR"),
                          tension: isHybrid
                            ? `${tensionMain}/${tensionCross} ${tensionUnit}`
                            : `${tensionMain} ${tensionUnit}`,
                          status: "aguardando",
                          type: "to_string" as any,
                          mainString,
                          crossString,
                          tensionMain,
                          tensionCross,
                          stringingType,
                          tensionUnit,
                          isHybrid,
                          racketId: selectedJobRacket,
                          isStringing,
                          preStretchMain,
                          preStretchCross,
                          basePrice: Number(price),
                          priceDiscountPercent:
                            priceDiscountPercent === ""
                              ? 0
                              : Number(priceDiscountPercent),
                          priceDiscountValue:
                            priceDiscountValue === ""
                              ? 0
                              : Number(priceDiscountValue),
                          price: finalPriceAux,
                          pickupDate,
                          commissionedProfessorId:
                            commissionedProfessorId || null,
                          commissionAction: commissionedProfessorId ? commissionAction : null,
                          auxServices,
                          stringerName: dashboardStringer,
                          pickupNotes,
                          stringingPoint: jobStringingPoint,
                          hasOwnReel,
                          hasOwnSet,
                          hasLogo,
                          logoNotes,
                          racketNotes,
                        };
                        setIsSavingMultiple(true);
                        try {
                          // SEMPRE criar novo job em vez de atualizar, mesmo em modo de edição
                          const res = await fetch(`${API_URL}/api/jobs`, {
                            method: "POST",
                            headers: {
                              ...getAuthHeader(),
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify(newJob),
                          });
                          if (!res.ok)
                            throw new Error(
                              "Falha ao salvar a raquete no servidor",
                            );

                          const savedJob = await res.json();
                          // Sempre adiciona no estado
                          setJobs((prev) => [savedJob, ...prev]);
                        } catch (err) {
                          console.error(err);
                          alert(
                            "Erro ao salvar a raquete. Por favor, tente novamente.",
                          );
                          setIsSavingMultiple(false);
                          return;
                        }
                        setIsSavingMultiple(false);

                        setEditingJobId(null);

                        setSelectedJobRacket("");
                        setMainString("");
                        setCrossString("");
                        setTensionMain("");
                        setTensionCross("");
                        setIsHybrid(false);
                        setIsStringing(true);
                        setPreStretchMain("");
                        setPreStretchCross("");
                        setPrice("");

                        // Re-aplica regras de desconto do clube (se aplicável), senão zera
                        let initDiscountPercent: number | "" = "";
                        let initDiscountValue: number | "" = "";
                        let initAux: {
                          type: string;
                          isActive: boolean;
                          price: number;
                          discountPercent: number | "";
                          discountValue: number | "";
                          notes: string;
                        }[] = [
                          {
                            type: "Trocar grip base",
                            isActive: false,
                            price: 0,
                            discountPercent: "",
                            discountValue: "",
                            notes: "",
                          },
                          {
                            type: "Trocar overgrip",
                            isActive: false,
                            price: 0,
                            discountPercent: "",
                            discountValue: "",
                            notes: "",
                          },
                          {
                            type: "Serviço customizado",
                            isActive: false,
                            price: 0,
                            discountPercent: "",
                            discountValue: "",
                            notes: "",
                          },
                          {
                            type: "Compra de raquete nova",
                            isActive: false,
                            price: 0,
                            discountPercent: "",
                            discountValue: "",
                            notes: "",
                          },
                          {
                            type: "Outros serviços",
                            isActive: false,
                            price: 0,
                            discountPercent: "",
                            discountValue: "",
                            notes: "",
                          },
                        ];

                        if (
                          selectedCustomer?.originClub &&
                          appSettings.clubDiscounts
                        ) {
                          const getDiscount = (service: string) => {
                            const todayStr = new Date()
                              .toISOString()
                              .split("T")[0];
                            const originClub = (
                              selectedCustomer.originClub || ""
                            )
                              .trim()
                              .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                            for (const d of appSettings.clubDiscounts) {
                              if (
                                d.club &&
                                d.club.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === originClub &&
                                (d.service === service || d.service === "Todos")
                              ) {
                                if (!d.startDate && !d.endDate) return d;
                                if (d.startDate && todayStr < d.startDate)
                                  continue;
                                if (d.endDate && todayStr > d.endDate) continue;
                                return d;
                              }
                            }
                            return null;
                          };
                          const mainD = getDiscount("Encordoamento");
                          if (mainD) {
                            initDiscountPercent = mainD.percent;
                            initDiscountValue = mainD.value;
                          }
                          initAux = initAux.map((s) => {
                            const d = getDiscount(s.type);
                            return {
                              ...s,
                              discountPercent: d ? d.percent : "",
                              discountValue: d ? d.value : "",
                            } as {
                              type: string;
                              isActive: boolean;
                              price: number;
                              discountPercent: number | "";
                              discountValue: number | "";
                              notes: string;
                            };
                          });
                        }

                        setPriceDiscountPercent(initDiscountPercent);
                        setPriceDiscountValue(initDiscountValue);
                        setAuxServices(initAux);

                        // Pequeno pulso visual para feedback de que a primeira foi salva
                        setJobSaved(true);
                        setTimeout(() => setJobSaved(false), 1000);
                      }
                    }}
                    style={{
                      padding: "16px 32px",
                      background: isSavingMultiple ? "gray" : "#D93B65",
                      border: "none",
                      borderRadius: "100px",
                      color: "white",
                      cursor: isSavingMultiple ? "not-allowed" : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {isSavingMultiple
                      ? "Salvando..."
                      : "Adicionar outra raquete"}
                  </button>
                  <button
                    type="submit"
                    className="button-primary"
                    style={{ padding: "16px 40px", fontSize: "16px" }}
                  >
                    {jobSaved ? (
                      <>
                        <CheckCircle size={20} /> Salvo!
                      </>
                    ) : (
                      "Finalizar Pedido"
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}
        {/* Stringing Execution View */}
        {view === "stringing" && activeStringingJob && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="glass-panel"
            style={{
              width: "100%",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <button
                  onClick={() => setView("dashboard")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    fontWeight: 800,
                    cursor: "pointer",
                    fontFamily: "var(--font-heading)",
                  }}
                >
                  Stringer Dashboard
                </button>
                <h2 style={{ fontSize: "24px", margin: 0, color: "white" }}>
                  {activeStringingJob.racketModel || "Head Speed Pro"}
                </h2>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  onClick={() => setView("dashboard")}
                  style={{
                    padding: "8px 16px",
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Fechar
                </button>
                <button
                  onClick={() => {
                    import("../utils/printUtils").then((m) =>
                      m.printLabel(
                        activeStringingJob,
                        "heart",
                        rackets?.find(
                          (r: any) => r.id === activeStringingJob.racketId,
                        ),
                      ),
                    );
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Imprimir etiqueta (coração)
                </button>

                <button
                  onClick={() => {
                    const cust = customers.find(
                      (c: any) => c.name === activeStringingJob.customerName,
                    );
                    startEditingJob(activeStringingJob, cust);
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "#4298E7",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Editar Encordoamento
                </button>
                <button
                  onClick={async () => {
                    const stringerToSave =
                      dashboardStringer || currentUser?.name || "Desconhecido";
                    const customDate = (
                      document.getElementById(
                        "stringingDateInput",
                      ) as HTMLInputElement
                    )?.value;
                    const previousJobs = [...jobs];
                    setJobs(
                      jobs.map((j) =>
                        j.id === activeStringingJob.id
                          ? {
                              ...j,
                              type: "picking_up",
                              status: "pronta",
                              stringerName: stringerToSave,
                              updatedAt: customDate || j.updatedAt,
                            }
                          : j,
                      ),
                    );
                    setView("dashboard");
                    setActiveFilter("picking_up");
                    try {
                      const res = await fetch(
                        `${API_URL}/api/jobs/${activeStringingJob.id}/status`,
                        {
                          method: "PUT",
                          headers: {
                            "Content-Type": "application/json",
                            ...getAuthHeader(),
                          },
                          body: JSON.stringify({
                            type: "picking_up",
                            status: "pronta",
                            stringerName: stringerToSave,
                            customUpdatedAt: customDate,
                          }),
                        },
                      );
                      if (!res.ok) throw new Error("Falha ao atualizar");
                    } catch (err) {
                      console.error(err);
                      alert("Erro ao atualizar o status no servidor.");
                      setJobs(previousJobs);
                    }
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "#6FCF97",
                    border: "none",
                    borderRadius: "8px",
                    color: "var(--text-dark)",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Finalizar Encordoamento
                </button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(300px, 1.2fr) 3fr",
                gap: "24px",
              }}
            >
              {/* Left Column Data Blocks */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    background: "rgba(111, 207, 151, 0.1)",
                    border: "1px solid rgba(111, 207, 151, 0.2)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6FCF97",
                      marginBottom: "4px",
                    }}
                  >
                    Cliente
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    {activeStringingJob.customerName}
                    {(() => {
                      const c = customers.find(
                        (c: any) => c.name === activeStringingJob.customerName,
                      );
                      return c?.numericId ? (
                        <span
                          style={{
                            fontSize: "14px",
                            color: "var(--text-secondary)",
                            marginLeft: "6px",
                          }}
                        >
                          (ID: {c.numericId})
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "var(--text-secondary)",
                      marginBottom: "4px",
                    }}
                  >
                    Data de retirada
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    {activeStringingJob.pickupDate
                      ? new Date(activeStringingJob.pickupDate).toLocaleString(
                          "pt-BR",
                          {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "Não agendada"}
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(155, 81, 224, 0.1)",
                    border: "1px solid rgba(155, 81, 224, 0.2)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#EBA6FF",
                      marginBottom: "4px",
                    }}
                  >
                    Tipo de Encordoamento
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    {activeStringingJob.stringingType || "Não definido"}
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(66, 152, 231, 0.1)",
                    border: "1px solid rgba(66, 152, 231, 0.2)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#4298E7",
                      marginBottom: "4px",
                    }}
                  >
                    Mains
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    {activeStringingJob.stringMains || "N/A"} @
                    {activeStringingJob.tensionMain ||
                      activeStringingJob.tension}{" "}
                    {activeStringingJob.tensionUnit || "Lbs"}
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(66, 152, 231, 0.1)",
                    border: "1px solid rgba(66, 152, 231, 0.2)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#4298E7",
                      marginBottom: "4px",
                    }}
                  >
                    Crosses
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    {activeStringingJob.stringCross ||
                      activeStringingJob.stringMains ||
                      "N/A"}{" "}
                    @
                    {activeStringingJob.tensionCross ||
                      activeStringingJob.tension}
                  </div>
                </div>
              </div>

              {/* Right Column Form & Dashboard */}
              <div
                style={{
                  background: "rgba(0,0,0,0.2)",
                  border: "1px solid rgba(255,255,255,0.05)",
                  borderRadius: "16px",
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  overflow: "hidden",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-secondary)",
                      fontSize: "14px",
                    }}
                  >
                    Notas do encordoamento
                  </label>
                  <textarea
                    rows={3}
                    style={{ ...inputStyle, resize: "none" }}
                  ></textarea>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Encordoador
                    </label>
                    <select
                      value={dashboardStringer}
                      onChange={(e) => setDashboardStringer(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Selecione...</option>
                      {[...(appSettings.stringers || [])]
                        .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                        .map((s: any) => {
                          const val = typeof s === 'string' ? s : s.name;
                          return (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Máquina
                    </label>
                    <select style={inputStyle}>
                      <option value="">Selecione...</option>
                      {[...(appSettings.machines || [])]
                        .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                        .map((m: string) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Data do encordoamento
                    </label>
                    <input
                      id="stringingDateInput"
                      type="datetime-local"
                      defaultValue={
                        activeStringingJob.createdAt
                          ? new Date(activeStringingJob.createdAt)
                              .toISOString()
                              .slice(0, 16)
                          : new Date(
                              Date.now() -
                                new Date().getTimezoneOffset() * 60000,
                            )
                              .toISOString()
                              .slice(0, 16)
                      }
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Frequência (HZ)
                    </label>
                    <input
                      type="number"
                      placeholder="Frequency"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Ert DT (DT)
                    </label>
                    <input
                      type="number"
                      placeholder="Ert DT"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                        fontSize: "14px",
                      }}
                    >
                      Chromatic (CH)
                    </label>
                    <input
                      type="number"
                      placeholder="Chromatic"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ marginTop: "16px" }}>
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "16px",
                      color: "white",
                    }}
                  >
                    Últimos encordoamentos da raquete
                  </h3>
                  <div
                    style={{
                      overflowX: "auto",
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div
                      style={{
                        minWidth: "800px",
                        display: "grid",
                        gridTemplateColumns:
                          "1.2fr 2fr 2fr 0.5fr 0.5fr 0.5fr 1fr 0.8fr 0.5fr",
                        padding: "16px",
                        fontWeight: 600,
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        background: "rgba(255,255,255,0.05)",
                        fontSize: "13px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <div>Data de ins.</div>
                      <div>Mains</div>
                      <div>Crosses</div>
                      <div>HZ</div>
                      <div>DT</div>
                      <div>CH</div>
                      <div>Encordoador</div>
                      <div>Preço</div>
                      <div>Horas</div>
                    </div>

                    {jobs.filter(
                      (j) => j.racketId === activeStringingJob.racketId,
                    ).length === 0 ? (
                      <div
                        style={{
                          padding: "32px",
                          textAlign: "center",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Nenhum encordoamento finalizado encontrado.
                      </div>
                    ) : (
                      jobs
                        .filter(
                          (j) => j.racketId === activeStringingJob.racketId,
                        )
                        .sort(
                          (a, b) =>
                            new Date(b.updatedAt).getTime() -
                            new Date(a.updatedAt).getTime(),
                        )
                        .map((pJob) => (
                          <div
                            key={pJob.id}
                            style={{
                              minWidth: "800px",
                              display: "grid",
                              gridTemplateColumns:
                                "1.2fr 2fr 2fr 0.5fr 0.5fr 0.5fr 1fr 0.8fr 0.5fr",
                              padding: "16px",
                              alignItems: "center",
                              borderBottom: "1px solid rgba(255,255,255,0.05)",
                              fontSize: "13px",
                              color: "white",
                            }}
                          >
                            <div>
                              {new Date(pJob.updatedAt).toLocaleString(
                                "pt-BR",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {pJob.stringMains || "N/A"}
                              </div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "var(--text-secondary)",
                                }}
                              >
                                @{pJob.tensionMain || pJob.tension}{" "}
                                {pJob.tensionUnit || "Lbs"}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {pJob.isHybrid
                                  ? pJob.stringCross ||
                                    pJob.stringMains ||
                                    "N/A"
                                  : pJob.stringMains || "N/A"}
                              </div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "var(--text-secondary)",
                                }}
                              >
                                @
                                {pJob.isHybrid
                                  ? pJob.tensionCross ||
                                    pJob.tensionMain ||
                                    pJob.tension
                                  : pJob.tensionMain || pJob.tension}{" "}
                                {pJob.tensionUnit || "Lbs"}
                              </div>
                            </div>
                            <div>-</div>
                            <div>-</div>
                            <div>-</div>
                            <div>
                              {pJob.stringerName ? pJob.stringerName : "-"}
                            </div>
                            <div style={{ fontWeight: 600 }}>
                              BRL{" "}
                              {pJob.price ? Number(pJob.price).toFixed(2) : "-"}
                            </div>
                            <div>-</div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <OrderDetailsView
          view={view}
          setView={setView}
          activeOrderJob={activeOrderJob}
          jobs={jobs}
          setJobs={setJobs}
          setActiveStringingJob={setActiveStringingJob}
          setActivePaymentJob={(job: any) => {
            setActivePaymentJob(job);
            setPaymentMode("racket");
          }}
          setIsPaymentModalOpen={setIsPaymentModalOpen}
          customers={customers}
          setSelectedCustomer={setSelectedCustomer}
          setNewJobStep={setNewJobStep}
          setActiveFilter={setActiveFilter}
          setIsCustomerModalOpen={setIsCustomerModalOpen}
          startEditingJob={startEditingJob}
          setCurrentOrderCode={setCurrentOrderCode}
          setEditingJobId={setEditingJobId}
          resetForm={resetForm}
          fetchCustomers={fetchCustomers}
          rackets={rackets}
        />

        {/* Customers View */}
        {view === "customers" && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            className="glass-panel"
            style={{ padding: "32px", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <button
                  onClick={() => setView("dashboard")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--primary-color)",
                    cursor: "pointer",
                  }}
                >
                  <ArrowLeft size={24} />
                </button>
                <h2 style={{ fontSize: "28px" }}>Base de Clientes</h2>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flex: 1,
                  justifyContent: "flex-end",
                  marginLeft: "24px",
                }}
              >
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  {activeCustomerFiltersCount > 0 && (
                    <button
                      onClick={() =>
                        setCustomerFilters({
                          name: "",
                          numericId: "",
                          originClub: "",
                          professorId: "",
                          email: "",
                          phone: "",
                          stringingPoint: "",
                        })
                      }
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#FECACA",
                        fontSize: "13px",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      Limpar Filtros
                    </button>
                  )}
                  <button
                    className="button-primary"
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      background:
                        activeCustomerFiltersCount > 0
                          ? "#6136B3"
                          : "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "none",
                      borderRadius: "24px",
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                    onClick={() => setIsCustomerSearchModalOpen(true)}
                  >
                    <Search size={16} /> Busca Avançada{" "}
                    {activeCustomerFiltersCount > 0 &&
                      `(${activeCustomerFiltersCount})`}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setView("dashboard")}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    color: "white",
                    border: "none",
                    padding: "8px 24px",
                    borderRadius: "24px",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Fechar
                </button>
                <div style={{ display: "flex", gap: "2px" }}>
                  <button
                    onClick={() => exportCustomersData("excel")}
                    style={{
                      background: "#6FCF97",
                      border: "none",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "4px 0 0 4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Gerar Excel"
                  >
                    <FileSpreadsheet size={16} />
                  </button>
                  <button
                    onClick={() => exportCustomersData("pdf")}
                    style={{
                      background: "#D93B65",
                      border: "none",
                      color: "white",
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Gerar PDF"
                  >
                    <FileText size={16} />
                  </button>
                  <button
                    onClick={() => exportCustomersData("csv")}
                    style={{
                      background: "#F2C94C",
                      border: "none",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "0 4px 4px 0",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Gerar CSV"
                  >
                    <FileJson size={16} />
                  </button>
                </div>
                <button
                  className="button-primary"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setIsCustomerModalOpen(true);
                  }}
                  style={{ padding: "8px 24px", fontSize: "14px" }}
                >
                  <UserPlus size={18} /> Adicionar Cliente
                </button>
              </div>
            </div>

            <div
              style={{
                background: "white",
                borderRadius: "12px",
                overflowX: "auto",
                boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                  color: "var(--text-dark)",
                  whiteSpace: "nowrap",
                }}
              >
                <thead>
                  <tr
                    style={{
                      color: "#6B7280",
                      fontSize: "13px",
                      borderBottom: "1px solid #E5E7EB",
                      background: "#FFFFFF",
                    }}
                  >
                    <th style={{ padding: "16px", fontWeight: 600 }}>Nome</th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>
                      ID TechTennis
                    </th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>
                      Ponto de Encordoamento
                    </th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>
                      Professor
                    </th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>Clube</th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>E-mail</th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>
                      Celular
                    </th>
                    <th style={{ padding: "16px", fontWeight: 600 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = customers.filter((c: any) => {
                      let matches = true;
                      if (customerFilters.name)
                        matches =
                          matches &&
                          (c.name || "")
                            .toLowerCase()
                            .includes(customerFilters.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                      if (customerFilters.email)
                        matches =
                          matches &&
                          (c.email || "")
                            .toLowerCase()
                            .includes(customerFilters.email.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                      if (customerFilters.phone)
                        matches =
                          matches &&
                          ((c.phone || "").includes(customerFilters.phone) ||
                            (c.landline || "").includes(customerFilters.phone));
                      if (customerFilters.originClub)
                        matches =
                          matches &&
                          c.originClub === customerFilters.originClub;
                      if (customerFilters.professorId)
                        matches =
                          matches &&
                          c.professorId === customerFilters.professorId;
                      if (customerFilters.stringingPoint)
                        matches =
                          matches &&
                          c.stringingPoint === customerFilters.stringingPoint;
                      if (customerFilters.numericId) {
                        const q = customerFilters.numericId.trim();
                        if (q.includes(",")) {
                          const parts = q.split(",").map((s) => s.trim());
                          matches =
                            matches && parts.includes(String(c.numericId));
                        } else if (q.includes("-")) {
                          const parts = q
                            .split("-")
                            .map((s) => parseInt(s.trim(), 10));
                          const min = parts[0];
                          const max = parts[1];
                          const val = parseInt(c.numericId, 10);
                          if (!isNaN(min) && !isNaN(max) && !isNaN(val)) {
                            matches = matches && val >= min && val <= max;
                          } else {
                            matches = false;
                          }
                        } else {
                          matches =
                            matches && String(c.numericId || "").includes(q);
                        }
                      }
                      return matches;
                    });
                    const totalItems = filtered.length;
                    const itemsPerPage =
                      customerItemsPerPage === "all"
                        ? totalItems
                        : customerItemsPerPage;
                    const startIndex = (customerCurrentPage - 1) * itemsPerPage;
                    const endIndex = Math.min(
                      startIndex + itemsPerPage,
                      totalItems,
                    );
                    const paginated =
                      itemsPerPage === totalItems
                        ? filtered
                        : filtered.slice(startIndex, endIndex);

                    return paginated.map((customer: any, index: number) => (
                      <tr
                        key={customer.id}
                        style={{
                          borderBottom: "1px solid #F3F4F6",
                          background: index % 2 === 0 ? "#F8F9FA" : "#FFFFFF",
                        }}
                      >
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          {customer.name}
                          {allSyncStatuses[customer.id]?.syncErrors && (
                            <span title="Erro de Sincronização. Clique em Editar para ver mais detalhes.">
                              <AlertCircle
                                size={16}
                                color="#FF4444"
                                style={{
                                  marginLeft: "8px",
                                  verticalAlign: "middle",
                                }}
                              />
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "16px", fontSize: "14px" }}>
                          {customer.numericId || "Não informado"}
                        </td>
                        <td style={{ padding: "16px", fontSize: "14px" }}>
                          {customer.stringingPoint || "Nenhum"}
                        </td>
                        <td style={{ padding: "16px", fontSize: "14px" }}>
                          {professors.find(
                            (p: any) => p.id === customer.professorId,
                          )?.name || "Não informado"}
                        </td>
                        <td style={{ padding: "16px", fontSize: "14px" }}>
                          {customer.originClub || "Não informado"}
                        </td>
                        <td style={{ padding: "16px", fontSize: "14px" }}>
                          {customer.email || "Não informado"}
                        </td>
                        <td style={{ padding: "16px", fontSize: "14px" }}>
                          {customer.phone ? applyPhoneMask(customer.phone) : ""}
                        </td>
                        <td style={{ padding: "16px" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setIsHistoryModalOpen(true);
                              }}
                              style={{
                                background: "#6136B3",
                                border: "none",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              title="Histórico"
                            >
                              <UserSquare size={16} />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm("Apagar cliente?")) {
                                  await fetch(
                                    `${API_URL}/api/customers/${customer.id}`,
                                    {
                                      method: "DELETE",
                                      headers: getAuthHeader(),
                                    },
                                  );
                                  if (import.meta.env.VITE_BFF_URL) {
                                    fetch(
                                      `${import.meta.env.VITE_BFF_URL}/api/clientes/${customer.id}`,
                                      { method: "DELETE" },
                                    ).catch(console.error);
                                  }
                                  fetchCustomers();
                                }
                              }}
                              style={{
                                background: "#D93B65",
                                border: "none",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setIsCustomerModalOpen(true);
                              }}
                              style={{
                                background: "#4298E7",
                                border: "none",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setCustomerQuery(customer.name);
                                setSelectedJobRacket("");
                                setNewJobStep(2);
                                setView("new_job");
                              }}
                              style={{
                                background: "#D93B65",
                                border: "none",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              title="Nova Ordem"
                            >
                              <FolderPlus size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>

              {/* Pagination footer */}
              {(() => {
                const filtered = customers.filter((c: any) => {
                  let matches = true;
                  if (customerFilters.name)
                    matches =
                      matches &&
                      (c.name || "")
                        .toLowerCase()
                        .includes(customerFilters.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                  if (customerFilters.email)
                    matches =
                      matches &&
                      (c.email || "")
                        .toLowerCase()
                        .includes(customerFilters.email.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                  if (customerFilters.phone)
                    matches =
                      matches &&
                      ((c.phone || "").includes(customerFilters.phone) ||
                        (c.landline || "").includes(customerFilters.phone));
                  if (customerFilters.originClub)
                    matches =
                      matches && c.originClub === customerFilters.originClub;
                  if (customerFilters.professorId)
                    matches =
                      matches && c.professorId === customerFilters.professorId;
                  if (customerFilters.stringingPoint)
                    matches =
                      matches &&
                      c.stringingPoint === customerFilters.stringingPoint;
                  if (customerFilters.numericId) {
                    const q = customerFilters.numericId.trim();
                    if (q.includes(",")) {
                      const parts = q.split(",").map((s) => s.trim());
                      matches = matches && parts.includes(String(c.numericId));
                    } else if (q.includes("-")) {
                      const parts = q
                        .split("-")
                        .map((s) => parseInt(s.trim(), 10));
                      const min = parts[0];
                      const max = parts[1];
                      const val = parseInt(c.numericId, 10);
                      if (!isNaN(min) && !isNaN(max) && !isNaN(val)) {
                        matches = matches && val >= min && val <= max;
                      } else {
                        matches = false;
                      }
                    } else {
                      matches =
                        matches && String(c.numericId || "").includes(q);
                    }
                  }
                  return matches;
                });
                const totalItems = filtered.length;
                const itemsPerPage =
                  customerItemsPerPage === "all"
                    ? totalItems
                    : customerItemsPerPage;
                const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
                const startIndex =
                  totalItems === 0
                    ? 0
                    : (customerCurrentPage - 1) * itemsPerPage + 1;
                const endIndex = Math.min(
                  (customerCurrentPage - 1) * itemsPerPage + itemsPerPage,
                  totalItems,
                );
                return (
                  <div
                    style={{
                      padding: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      color: "#6B7280",
                      fontSize: "13px",
                      background: "white",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span>Show</span>
                      <select
                        value={customerItemsPerPage}
                        onChange={(e) => {
                          setCustomerItemsPerPage(
                            e.target.value === "all"
                              ? "all"
                              : parseInt(e.target.value),
                          );
                          setCustomerCurrentPage(1);
                        }}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #E5E7EB",
                          background: "#F8F9FA",
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={40}>40</option>
                        <option value={50}>50</option>
                        <option value="all">Todas</option>
                      </select>
                      <span>entries</span>
                      <span style={{ marginLeft: "16px" }}>
                        Showing {startIndex} to {endIndex} of {totalItems}{" "}
                        entries
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={() =>
                          setCustomerCurrentPage(
                            Math.max(1, customerCurrentPage - 1),
                          )
                        }
                        disabled={customerCurrentPage === 1}
                        style={{
                          border: "none",
                          background: "none",
                          color:
                            customerCurrentPage === 1 ? "#9CA3AF" : "#4298E7",
                          cursor:
                            customerCurrentPage === 1
                              ? "not-allowed"
                              : "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Previous
                      </button>
                      <button
                        style={{
                          border: "none",
                          background: "#4298E7",
                          color: "white",
                          width: "28px",
                          height: "28px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {customerCurrentPage}
                      </button>
                      <button
                        onClick={() =>
                          setCustomerCurrentPage(
                            Math.min(totalPages, customerCurrentPage + 1),
                          )
                        }
                        disabled={customerCurrentPage === totalPages}
                        style={{
                          border: "none",
                          background: "none",
                          color:
                            customerCurrentPage === totalPages
                              ? "#9CA3AF"
                              : "#4298E7",
                          cursor:
                            customerCurrentPage === totalPages
                              ? "not-allowed"
                              : "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </motion.div>
        )}

        {/* Professors View */}
        {view === "professors" && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            className="glass-panel"
            style={{ padding: "32px", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <button
                  onClick={() => setView("dashboard")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--primary-color)",
                    cursor: "pointer",
                  }}
                >
                  <ArrowLeft size={24} />
                </button>
                <h2 style={{ fontSize: "28px" }}>Professores Comissionados</h2>
              </div>
              <div
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <button
                  type="button"
                  onClick={() => setView("dashboard")}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    color: "white",
                    border: "none",
                    padding: "8px 24px",
                    borderRadius: "24px",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Fechar
                </button>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  {activeProfessorFiltersCount > 0 && (
                    <button
                      onClick={() =>
                        setProfessorFilters({
                          name: "",
                          numericId: "",
                          email: "",
                          phone: "",
                        })
                      }
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#FECACA",
                        fontSize: "13px",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      Limpar Filtros
                    </button>
                  )}
                  <button
                    className="button-primary"
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      background:
                        activeProfessorFiltersCount > 0
                          ? "#6136B3"
                          : "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "none",
                      borderRadius: "24px",
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                    onClick={() => setIsProfessorSearchModalOpen(true)}
                  >
                    <Search size={16} /> Busca Avançada{" "}
                    {activeProfessorFiltersCount > 0 &&
                      `(${activeProfessorFiltersCount})`}
                  </button>
                </div>
                <div
                  style={{ display: "flex", gap: "2px", marginRight: "12px" }}
                >
                  <button
                    onClick={() => exportProfessorsData("excel")}
                    style={{
                      background: "#6FCF97",
                      border: "none",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "4px 0 0 4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Gerar Excel"
                  >
                    <FileSpreadsheet size={16} />
                  </button>
                  <button
                    onClick={() => exportProfessorsData("pdf")}
                    style={{
                      background: "#D93B65",
                      border: "none",
                      color: "white",
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Gerar PDF"
                  >
                    <FileText size={16} />
                  </button>
                  <button
                    onClick={() => exportProfessorsData("csv")}
                    style={{
                      background: "#F2C94C",
                      border: "none",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "0 4px 4px 0",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Gerar CSV"
                  >
                    <FileJson size={16} />
                  </button>
                </div>
                <button
                  className="button-primary"
                  onClick={() => {
                    setSelectedProfessor(null);
                    setIsProfessorModalOpen(true);
                  }}
                  style={{ padding: "8px 24px", fontSize: "14px" }}
                >
                  <UserPlus size={18} /> Novo Professor
                </button>
              </div>
            </div>

            <div
              style={{
                background: "white",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                  color: "var(--text-dark)",
                }}
              >
                <thead>
                  <tr
                    style={{
                      color: "#6B7280",
                      fontSize: "13px",
                      borderBottom: "1px solid #E5E7EB",
                      background: "#FFFFFF",
                    }}
                  >
                    <th style={{ padding: "16px", fontWeight: 600 }}>Nome</th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>
                      ID TechTennis
                    </th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>E-mail</th>
                    <th style={{ padding: "16px", fontWeight: 600 }}>
                      Celular
                    </th>
                    <th style={{ padding: "16px", fontWeight: 600 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = professors.filter((p: any) => {
                      let matches = true;
                      if (professorFilters.name)
                        matches =
                          matches &&
                          (p.name || "")
                            .toLowerCase()
                            .includes(professorFilters.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                      if (professorFilters.email)
                        matches =
                          matches &&
                          (p.email || "")
                            .toLowerCase()
                            .includes(professorFilters.email.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                      if (professorFilters.phone)
                        matches =
                          matches &&
                          (p.phone || "").includes(professorFilters.phone);
                      if (professorFilters.numericId) {
                        const q = professorFilters.numericId.trim();
                        if (q.includes(",")) {
                          const parts = q.split(",").map((s) => s.trim());
                          matches =
                            matches && parts.includes(String(p.numericId));
                        } else if (q.includes("-")) {
                          const parts = q
                            .split("-")
                            .map((s) => parseInt(s.trim(), 10));
                          const min = parts[0];
                          const max = parts[1];
                          const val = parseInt(p.numericId, 10);
                          if (!isNaN(min) && !isNaN(max) && !isNaN(val)) {
                            matches = matches && val >= min && val <= max;
                          } else {
                            matches = false;
                          }
                        } else {
                          matches =
                            matches && String(p.numericId || "").includes(q);
                        }
                      }
                      return matches;
                    });

                    const totalItems = filtered.length;
                    const itemsPerPage =
                      professorItemsPerPage === "all"
                        ? totalItems
                        : professorItemsPerPage;
                    const startIndex =
                      totalItems === 0
                        ? 0
                        : (professorCurrentPage - 1) * itemsPerPage;
                    const endIndex = Math.min(
                      startIndex + itemsPerPage,
                      totalItems,
                    );
                    const paginated =
                      itemsPerPage === totalItems
                        ? filtered
                        : filtered.slice(startIndex, endIndex);

                    return (
                      <>
                        {paginated.map((prof: any, index: number) => (
                          <tr
                            key={prof.id}
                            style={{
                              borderBottom: "1px solid #F3F4F6",
                              background:
                                index % 2 === 0 ? "#F8F9FA" : "#FFFFFF",
                            }}
                          >
                            <td
                              style={{
                                padding: "16px",
                                fontSize: "14px",
                                fontWeight: 600,
                              }}
                            >
                              {prof.name}
                              {allSyncStatuses[prof.id]?.syncErrors && (
                                <span title="Erro de Sincronização. Clique em Editar para ver mais detalhes.">
                                  <AlertCircle
                                    size={16}
                                    color="#FF4444"
                                    style={{
                                      marginLeft: "8px",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "16px", fontSize: "14px" }}>
                              {prof.numericId || ""}
                            </td>
                            <td style={{ padding: "16px", fontSize: "14px" }}>
                              {prof.email || ""}
                            </td>
                            <td style={{ padding: "16px", fontSize: "14px" }}>
                              {prof.phone ? applyPhoneMask(prof.phone) : ""}
                            </td>
                            <td style={{ padding: "16px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  onClick={async () => {
                                    if (window.confirm("Apagar professor?")) {
                                      await fetch(
                                        `${API_URL}/api/professors/${prof.id}`,
                                        {
                                          method: "DELETE",
                                          headers: getAuthHeader(),
                                        },
                                      );
                                      fetchProfessors();
                                    }
                                  }}
                                  style={{
                                    background: "#D93B65",
                                    border: "none",
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "6px",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                  }}
                                  title="Excluir"
                                >
                                  <Trash2 size={16} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedProfessor(prof);
                                    setIsProfessorModalOpen(true);
                                  }}
                                  style={{
                                    background: "#4298E7",
                                    border: "none",
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "6px",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                  }}
                                  title="Editar"
                                >
                                  <Edit size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })()}
                </tbody>
              </table>

              {(() => {
                const filtered = professors.filter((p: any) => {
                  let matches = true;
                  if (professorFilters.name)
                    matches =
                      matches &&
                      (p.name || "")
                        .toLowerCase()
                        .includes(professorFilters.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                  if (professorFilters.email)
                    matches =
                      matches &&
                      (p.email || "")
                        .toLowerCase()
                        .includes(professorFilters.email.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
                  if (professorFilters.phone)
                    matches =
                      matches &&
                      (p.phone || "").includes(professorFilters.phone);
                  if (professorFilters.numericId) {
                    const q = professorFilters.numericId.trim();
                    if (q.includes(",")) {
                      const parts = q.split(",").map((s) => s.trim());
                      matches = matches && parts.includes(String(p.numericId));
                    } else if (q.includes("-")) {
                      const parts = q
                        .split("-")
                        .map((s) => parseInt(s.trim(), 10));
                      const min = parts[0];
                      const max = parts[1];
                      const val = parseInt(p.numericId, 10);
                      if (!isNaN(min) && !isNaN(max) && !isNaN(val)) {
                        matches = matches && val >= min && val <= max;
                      } else {
                        matches = false;
                      }
                    } else {
                      matches =
                        matches && String(p.numericId || "").includes(q);
                    }
                  }
                  return matches;
                });
                const totalItems = filtered.length;
                const itemsPerPage =
                  professorItemsPerPage === "all"
                    ? totalItems
                    : professorItemsPerPage;
                const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
                const startIndex =
                  totalItems === 0
                    ? 0
                    : (professorCurrentPage - 1) * itemsPerPage + 1;
                const endIndex = Math.min(
                  (professorCurrentPage - 1) * itemsPerPage + itemsPerPage,
                  totalItems,
                );
                return (
                  <div
                    style={{
                      padding: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      color: "#6B7280",
                      fontSize: "13px",
                      background: "white",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span>Show</span>
                      <select
                        value={professorItemsPerPage}
                        onChange={(e) => {
                          setProfessorItemsPerPage(
                            e.target.value === "all"
                              ? "all"
                              : parseInt(e.target.value),
                          );
                          setProfessorCurrentPage(1);
                        }}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #E5E7EB",
                          background: "#F8F9FA",
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={40}>40</option>
                        <option value={50}>50</option>
                        <option value="all">Todas</option>
                      </select>
                      <span>entries</span>
                      <span style={{ marginLeft: "16px" }}>
                        Showing {startIndex} to {endIndex} of {totalItems}{" "}
                        entries
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={() =>
                          setProfessorCurrentPage(
                            Math.max(1, professorCurrentPage - 1),
                          )
                        }
                        disabled={professorCurrentPage === 1}
                        style={{
                          border: "none",
                          background: "none",
                          color:
                            professorCurrentPage === 1 ? "#9CA3AF" : "#4298E7",
                          cursor:
                            professorCurrentPage === 1
                              ? "not-allowed"
                              : "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Previous
                      </button>
                      <button
                        style={{
                          border: "none",
                          background: "#4298E7",
                          color: "white",
                          width: "28px",
                          height: "28px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {professorCurrentPage}
                      </button>
                      <button
                        onClick={() =>
                          setProfessorCurrentPage(
                            Math.min(totalPages, professorCurrentPage + 1),
                          )
                        }
                        disabled={professorCurrentPage === totalPages}
                        style={{
                          border: "none",
                          background: "none",
                          color:
                            professorCurrentPage === totalPages
                              ? "#9CA3AF"
                              : "#4298E7",
                          cursor:
                            professorCurrentPage === totalPages
                              ? "not-allowed"
                              : "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </motion.div>
        )}

        {view === "analytics" && (
          <AnalyticsView
            jobs={filteredJobs}
            appSettings={appSettings}
            customers={customers}
            professors={professors}
            rackets={rackets}
          />
        )}
        {view === "orders" && (
          <OrdersView
            jobs={jobs}
            customers={customers}
            professors={professors}
            onAddOrder={() => {
              setView("new_job");
              setNewJobStep(1);
              setSelectedCustomer(null);
              setCustomerQuery("");
              setSelectedJobRacket("");
            }}
            onDeleteOrder={async (orderCode: string) => {
              if (
                window.confirm(
                  "Tem certeza que deseja excluir esta ordem inteira e todas as suas raquetes da Nuvem?",
                )
              ) {
                const jobsToDelete = jobs.filter(
                  (j) =>
                    (j.orderCode || j.id.substring(0, 8).toUpperCase()) ===
                    orderCode,
                );
                for (const j of jobsToDelete) {
                  await fetch(`${API_URL}/api/jobs/${j.id}`, {
                    method: "DELETE",
                    headers: getAuthHeader(),
                  });
                }
                if (import.meta.env.VITE_BFF_URL) {
                  fetch(
                    `${import.meta.env.VITE_BFF_URL}/api/pedidos/${orderCode}`,
                    { method: "DELETE" },
                  ).catch(console.error);
                }
                fetchJobs();
              }
            }}
            onEditOrder={(orderCode: string) => {
              const job = jobs.find(
                (j) =>
                  (j.orderCode || j.id.substring(0, 8).toUpperCase()) ===
                  orderCode,
              );
              if (job) {
                const cust = customers.find((c) => c.name === job.customerName);
                startEditingJob(job, cust);
              }
            }}
            onPayment={(orderCode: string) => {
              const job = jobs.find(
                (j) =>
                  (j.orderCode || j.id.substring(0, 8).toUpperCase()) ===
                  orderCode,
              );
              if (job) {
                setActivePaymentJob(job);
                setPaymentMode("order");
                setIsPaymentModalOpen(true);
              }
            }}
            onDelivery={(orderCode: string) => {
              const job = jobs.find(
                (j) =>
                  (j.orderCode || j.id.substring(0, 8).toUpperCase()) ===
                  orderCode,
              );
              if (job) {
                setActivePickupJob(job);
                setIsPickupModalOpen(true);
              }
            }}
            onViewOrder={(orderCode: string) => {
              const job = jobs.find(
                (j) =>
                  (j.orderCode || j.id.substring(0, 8).toUpperCase()) ===
                  orderCode,
              );
              if (job) {
                setActiveOrderJob(job);
                setView("order_details");
              }
            }}
            onViewFeedback={(orderCode: string) => {
              const jobWithFeedback = jobs.find(
                (j) =>
                  (j.orderCode || j.id.substring(0, 8).toUpperCase()) ===
                    orderCode && j.feedback,
              );
              if (jobWithFeedback) {
                setActiveFeedbackJob(jobWithFeedback);
                setIsFeedbackModalOpen(true);
              }
            }}
          />
        )}

        {view === "settings" && (
          <SettingsView settings={appSettings} setSettings={setAppSettings} />
        )}
      </div>

      {/* Customer Advanced Search Modal */}
      <AnimatePresence>
        {isCustomerSearchModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(4px)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                width: "100%",
                maxWidth: "700px",
                background: "var(--bg-panel)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              }}
            >
              <div
                style={{
                  background: "#6136B3",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    color: "white",
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: 0,
                  }}
                >
                  Pesquisa Avançada de Clientes
                </h3>
                <button
                  onClick={() => setIsCustomerSearchModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ padding: "32px", overflowY: "auto" }}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setIsCustomerSearchModalOpen(false);
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        Nome
                      </label>
                      <input
                        type="text"
                        value={customerFilters.name}
                        onChange={(e) =>
                          setCustomerFilters({
                            ...customerFilters,
                            name: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Parte do nome..."
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        ID TechTennis (Múltiplos ou Range)
                      </label>
                      <input
                        type="text"
                        value={customerFilters.numericId}
                        onChange={(e) =>
                          setCustomerFilters({
                            ...customerFilters,
                            numericId: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Ex: 1045, 1046 ou 1045-1050"
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        Professor / Treinador
                      </label>
                      <select
                        value={customerFilters.professorId}
                        onChange={(e) =>
                          setCustomerFilters({
                            ...customerFilters,
                            professorId: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                      >
                        <option value="">Todos</option>
                        {professors.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        Clube
                      </label>
                      <select
                        value={customerFilters.originClub}
                        onChange={(e) =>
                          setCustomerFilters({
                            ...customerFilters,
                            originClub: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                      >
                        <option value="">Todos</option>
                        {[...(appSettings.clubs || [])]
                          .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                          .map((c: string) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        E-mail
                      </label>
                      <input
                        type="text"
                        value={customerFilters.email}
                        onChange={(e) =>
                          setCustomerFilters({
                            ...customerFilters,
                            email: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Email..."
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        Celular
                      </label>
                      <input
                        type="text"
                        value={customerFilters.phone}
                        onChange={(e) =>
                          setCustomerFilters({
                            ...customerFilters,
                            phone: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Telefone..."
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "16px",
                      marginTop: "12px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setCustomerFilters({
                          name: "",
                          numericId: "",
                          originClub: "",
                          professorId: "",
                          email: "",
                          phone: "",
                          stringingPoint: "",
                        })
                      }
                      style={{
                        padding: "12px 24px",
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.4)",
                        borderRadius: "24px",
                        color: "white",
                        cursor: "pointer",
                      }}
                    >
                      Limpar
                    </button>
                    <button
                      type="submit"
                      className="button-primary"
                      style={{ padding: "12px 24px", borderRadius: "24px" }}
                    >
                      Aplicar Filtros
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Professor Advanced Search Modal */}
      <AnimatePresence>
        {isProfessorSearchModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(4px)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                width: "100%",
                maxWidth: "700px",
                background: "var(--bg-panel)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              }}
            >
              <div
                style={{
                  background: "#6136B3",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    color: "white",
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: 0,
                  }}
                >
                  Pesquisa Avançada de Professores
                </h3>
                <button
                  onClick={() => setIsProfessorSearchModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ padding: "32px", overflowY: "auto" }}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setIsProfessorSearchModalOpen(false);
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        Nome
                      </label>
                      <input
                        type="text"
                        value={professorFilters.name}
                        onChange={(e) =>
                          setProfessorFilters({
                            ...professorFilters,
                            name: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Parte do nome..."
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        ID TechTennis (Múltiplos ou Range)
                      </label>
                      <input
                        type="text"
                        value={professorFilters.numericId}
                        onChange={(e) =>
                          setProfessorFilters({
                            ...professorFilters,
                            numericId: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Ex: 1045, 1046 ou 1045-1050"
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        E-mail
                      </label>
                      <input
                        type="text"
                        value={professorFilters.email}
                        onChange={(e) =>
                          setProfessorFilters({
                            ...professorFilters,
                            email: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Email..."
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "white",
                        }}
                      >
                        Celular
                      </label>
                      <input
                        type="text"
                        value={professorFilters.phone}
                        onChange={(e) =>
                          setProfessorFilters({
                            ...professorFilters,
                            phone: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "rgba(0,0,0,0.1)",
                          color: "white",
                        }}
                        placeholder="Telefone..."
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "16px",
                      marginTop: "12px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setProfessorFilters({
                          name: "",
                          numericId: "",
                          email: "",
                          phone: "",
                        })
                      }
                      style={{
                        padding: "12px 24px",
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.4)",
                        borderRadius: "24px",
                        color: "white",
                        cursor: "pointer",
                      }}
                    >
                      Limpar
                    </button>
                    <button
                      type="submit"
                      className="button-primary"
                      style={{ padding: "12px 24px", borderRadius: "24px" }}
                    >
                      Aplicar Filtros
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Customer Modal */}
      <AnimatePresence>
        {isCustomerModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(4px)",
              zIndex: 100,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                width: "100%",
                maxWidth: "900px",
                maxHeight: "90vh",
                background: "var(--bg-panel)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: "#8F5FFF",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    color: "white",
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: 0,
                  }}
                >
                  {selectedCustomer ? "Editar Cliente" : "Adicionar Cliente"}
                </h3>
                <button
                  onClick={() => {
                    setIsCustomerModalOpen(false);
                    setSelectedCustomer(null);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: "32px", overflowY: "auto", flex: 1 }}>
                {customerSyncStatusData &&
                  customerSyncStatusData.syncErrors && (
                    <div
                      style={{
                        marginBottom: "24px",
                        padding: "16px",
                        background: "rgba(255, 68, 68, 0.1)",
                        border: "1px solid #FF4444",
                        borderRadius: "8px",
                        color: "#FF4444",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <AlertCircle size={20} />
                        Erro de Sincronização com o Bling
                      </div>
                      <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                        {(() => {
                          try {
                            const parsed = JSON.parse(
                              customerSyncStatusData.syncErrors as string,
                            );
                            if (parsed.error && parsed.error.fields) {
                              return (
                                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                  {parsed.error.fields.map(
                                    (f: any, idx: number) => (
                                      <li key={idx}>{f.msg}</li>
                                    ),
                                  )}
                                </ul>
                              );
                            }
                            return (
                              parsed.error?.message ||
                              customerSyncStatusData.syncErrors
                            );
                          } catch {
                            return customerSyncStatusData.syncErrors;
                          }
                        })()}
                      </div>
                    </div>
                  )}
                <form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const cpfCnpjValue = fd.get("cpfCnpj") as string;
                    if (cpfCnpjValue && !validateCpfCnpj(cpfCnpjValue)) {
                      setCpfCnpjError(true);
                      return;
                    }
                    setCpfCnpjError(false);
                    const firstName = fd.get("firstName") as string;
                    const lastName = fd.get("lastName") as string;
                    const emailValue = fd.get("email") as string;
                    const phoneValue = fd.get("phone") as string;

                    const customerData = {
                      name: `${firstName} ${lastName}`.trim(),
                      phone:
                        phoneValue && phoneValue.trim() !== ""
                          ? phoneValue
                          : "",
                      email:
                        emailValue && emailValue.trim() !== ""
                          ? emailValue
                          : "nao.informado@techtennis.com",
                      originClub: fd.get("originClub") as string,
                      gender: fd.get("gender") as string,
                      professorId: fd.get("professorId") as string,
                      birthDate: fd.get("birthDate") as string,
                      cpfCnpj: fd.get("cpfCnpj") as string,
                      landline: fd.get("landline") as string,
                      address: fd.get("address") as string,
                      addressNumber: fd.get("addressNumber") as string,
                      complement: fd.get("complement") as string,
                      neighborhood: fd.get("neighborhood") as string,
                      state: fd.get("state") as string,
                      zipCode: fd.get("zipCode") as string,
                      cep: fd.get("cep") as string,
                      city: fd.get("city") as string,
                      country: fd.get("country") as string,
                      stringingPoint: fd.get("stringingPoint") as string,
                      notes: fd.get("notes") as string,
                      numericId: fd.get("numericId")
                        ? parseInt(fd.get("numericId") as string, 10)
                        : undefined,
                    };
                    try {
                      if (selectedCustomer) {
                        const res = await fetch(
                          `${API_URL}/api/customers/${selectedCustomer.id}`,
                          {
                            method: "PUT",
                            headers: {
                              ...getAuthHeader(),
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify(customerData),
                          },
                        );
                        if (!res.ok)
                          throw new Error((await res.text()) || "API Error");

                        if (import.meta.env.VITE_BFF_URL) {
                          fetch(
                            `${import.meta.env.VITE_BFF_URL}/api/clientes/${selectedCustomer.id}`,
                            {
                              method: "PUT",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                name: customerData.name,
                                numericId: customerData.numericId,
                                email: customerData.email,
                                phone: customerData.phone,
                                document: customerData.cpfCnpj || "",
                                address: customerData.address || "",
                                addressNumber: customerData.addressNumber || "",
                                complement: customerData.complement || "",
                                neighborhood: customerData.neighborhood || "",
                                city: customerData.city || "",
                                state: customerData.state || "",
                                zipCode:
                                  customerData.zipCode ||
                                  customerData.cep ||
                                  "",
                              }),
                            },
                          ).catch(console.error);
                        }

                        await fetchCustomers();
                      } else {
                        const res = await fetch(`${API_URL}/api/customers`, {
                          method: "POST",
                          headers: {
                            ...getAuthHeader(),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify(customerData),
                        });
                        if (!res.ok)
                          throw new Error((await res.text()) || "API Error");
                        const savedCustomer = await res.json();

                        if (import.meta.env.VITE_BFF_URL) {
                          try {
                            await fetch(
                              `${import.meta.env.VITE_BFF_URL}/api/clientes`,
                              {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  id: savedCustomer?.id,
                                  numericId:
                                    customerData.numericId ||
                                    savedCustomer?.numericId,
                                  name: customerData.name,
                                  email: customerData.email,
                                  phone: customerData.phone,
                                  document: customerData.cpfCnpj || "",
                                  address: customerData.address || "",
                                  addressNumber:
                                    customerData.addressNumber || "",
                                  complement: customerData.complement || "",
                                  neighborhood: customerData.neighborhood || "",
                                  city: customerData.city || "",
                                  state: customerData.state || "",
                                  zipCode:
                                    customerData.zipCode ||
                                    customerData.cep ||
                                    "",
                                }),
                              },
                            );
                          } catch (bffErr) {
                            console.error(
                              "Erro ao sincronizar com BFF:",
                              bffErr,
                            );
                          }
                        }

                        await fetchCustomers();
                        setSelectedCustomer(savedCustomer);
                        setCustomerQuery(savedCustomer.name);
                      }
                      setIsCustomerModalOpen(false);
                    } catch (err: any) {
                      console.error("Error saving customer:", err);
                      alert("Erro ao salvar cliente. Detalhes: " + err.message);
                    }
                  }}
                >
                  <div style={{ marginBottom: "24px" }}>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="numericId"
                      label="ID TechTennis (Automático)"
                    >
                      {(status) => (
                        <input
                          name="numericId"
                          type="number"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                          defaultValue={selectedCustomer?.numericId || ""}
                          placeholder="Ex: 104151 (ou deixe em branco para gerar novo)"
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="lastName"
                      label="Sobrenome"
                    >
                      {(status) => (
                        <input
                          name="lastName"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                          defaultValue={
                            selectedCustomer
                              ? selectedCustomer.name
                                  ?.split(" ")
                                  .slice(1)
                                  .join(" ")
                              : ""
                          }
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="firstName"
                      label="Nome"
                    >
                      {(status) => (
                        <input
                          name="firstName"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                          defaultValue={
                            selectedCustomer
                              ? selectedCustomer.name?.split(" ")[0]
                              : ""
                          }
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="gender"
                      label="Gênero"
                    >
                      {(status) => (
                        <select
                          name="gender"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.gender || ""}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                        >
                          <option value="">Selecione...</option>
                          <option value="M">Masculino</option>
                          <option value="F">Feminino</option>
                          <option value="O">Outro</option>
                        </select>
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="phone"
                      label="Celular"
                    >
                      {(status) => (
                        <input
                          name="phone"
                          type="tel"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                          onChange={(e) =>
                            (e.target.value = applyPhoneMask(e.target.value))
                          }
                          defaultValue={
                            selectedCustomer && selectedCustomer.phone
                              ? applyPhoneMask(selectedCustomer.phone)
                              : ""
                          }
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="originClub"
                      label="Clube de Origem"
                    >
                      {(status) => (
                        <select
                          name="originClub"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.originClub || ""}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                        >
                          <option value="">Não informado</option>
                          {Array.from(
                            new Set([
                              ...(appSettings.clubs || []),
                              ...(selectedCustomer?.originClub
                                ? [selectedCustomer.originClub]
                                : []),
                            ]),
                          )
                            .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                            .map((club: any) => (
                              <option key={club} value={club}>
                                {club}
                              </option>
                            ))}
                        </select>
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="professorId"
                      label="Professor / Treinador"
                    >
                      {(status) => (
                        <select
                          name="professorId"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.professorId || ""}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                        >
                          <option value="">Selecione um professor...</option>
                          {professors.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="email"
                      label="E-mail"
                    >
                      {(status) => (
                        <input
                          name="email"
                          type="email"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                          defaultValue={
                            selectedCustomer ? selectedCustomer.email : ""
                          }
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="birthDate"
                      label="Data de Nascimento"
                    >
                      {(status) => (
                        <input
                          name="birthDate"
                          type="date"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.birthDate || ""}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "rgba(0,0,0,0.1)",
                            color: "white",
                            fontSize: "15px",
                          }}
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="cpfCnpj"
                      label="CPF / CNPJ"
                    >
                      {(status) => (
                        <>
                          <input
                            name="cpfCnpj"
                            type="text"
                            disabled={status === "readonly"}
                            required={status === "mandatory"}
                            onChange={(e) => {
                              e.target.value = applyCpfCnpjMask(e.target.value);
                              setCpfCnpjError(false);
                            }}
                            defaultValue={
                              selectedCustomer?.cpfCnpj
                                ? applyCpfCnpjMask(selectedCustomer.cpfCnpj)
                                : ""
                            }
                            style={{
                              ...inputStyle,
                              borderColor: cpfCnpjError
                                ? "#D93B65"
                                : "var(--border-light)",
                            }}
                          />
                          {cpfCnpjError && (
                            <span
                              style={{
                                color: "#D93B65",
                                fontSize: "13px",
                                marginTop: "6px",
                                display: "block",
                              }}
                            >
                              CPF ou CNPJ inválido.
                            </span>
                          )}
                        </>
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="cep"
                      label="CEP"
                    >
                      {(status) => (
                        <input
                          name="cep"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          onBlur={handleCepBlur}
                          defaultValue={selectedCustomer?.cep || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="address"
                      label="Endereço"
                    >
                      {(status) => (
                        <input
                          name="address"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.address || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="addressNumber"
                      label="Número"
                    >
                      {(status) => (
                        <input
                          name="addressNumber"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.addressNumber || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="complement"
                      label="Complemento"
                    >
                      {(status) => (
                        <input
                          name="complement"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.complement || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="neighborhood"
                      label="Bairro"
                    >
                      {(status) => (
                        <input
                          name="neighborhood"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.neighborhood || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="city"
                      label="Cidade"
                    >
                      {(status) => (
                        <input
                          name="city"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.city || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="state"
                      label="Estado"
                    >
                      {(status) => (
                        <input
                          name="state"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.state || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="country"
                      label="País"
                    >
                      {(status) => (
                        <input
                          name="country"
                          type="text"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.country || "Brasil"}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="customerFields"
                      field="stringingPoint"
                      label="Ponto de Encordoamento Base"
                    >
                      {(status) => (
                        <select
                          name="stringingPoint"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedCustomer?.stringingPoint || ""}
                          style={inputStyle}
                        >
                          <option value="">Selecione...</option>
                          {(() => {
                            const cur = selectedCustomer?.stringingPoint;
                            const opts = [...availablePointsForUser];
                            if (cur && !opts.includes(cur)) {
                              opts.unshift(cur);
                            }
                            return opts.map((p: string) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ));
                          })()}
                        </select>
                      )}
                    </FieldWrapper>
                  </div>

                  <FieldWrapper
                    appSettings={appSettings}
                    type="customerFields"
                    field="notes"
                    label="Observações"
                  >
                    {(status) => (
                      <textarea
                        name="notes"
                        disabled={status === "readonly"}
                        required={status === "mandatory"}
                        defaultValue={selectedCustomer?.notes || ""}
                        rows={3}
                        style={{ ...inputStyle, resize: "none" }}
                      ></textarea>
                    )}
                  </FieldWrapper>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "16px",
                      marginTop: "24px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsCustomerModalOpen(false)}
                      style={{
                        padding: "16px 32px",
                        background: "transparent",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderRadius: "100px",
                        color: "white",
                        cursor: "pointer",
                        fontWeight: 700,
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="button-primary"
                      style={{ padding: "16px 32px" }}
                    >
                      Salvar Cliente
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {isHistoryModalOpen && (
          <CustomerHistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            customer={selectedCustomer}
            jobs={jobs}
            setJobs={setJobs}
            onEdit={(job: any) => {
              setIsHistoryModalOpen(false);
              setCustomerQuery(job.customerName);
              setSelectedJobRacket(job.racketModel);
              setNewJobStep(2);
              setView("new_job");
            }}
          />
        )}
      </AnimatePresence>

      {/* Add Racket Modal */}
      <AnimatePresence>
        {isRacketModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,12,60,0.8)",
              backdropFilter: "blur(8px)",
              zIndex: 100,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                width: "100%",
                maxWidth: "900px",
                maxHeight: "90vh",
                background: "var(--bg-panel-solid)",
                borderRadius: "32px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: "#3A52EE",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    color: "white",
                    fontSize: "20px",
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  {racketFormDefault?.isClone
                    ? "Clonar Raquete"
                    : "Adicionar Raquete"}
                </h3>
                <button
                  onClick={() => setIsRacketModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Body */}
              <div
                style={{
                  padding: "32px",
                  overflowY: "auto",
                  flex: 1,
                  background: "rgba(255,255,255,0.05)",
                }}
              >
                <form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const racketName = fd.get("racketName") as string;
                    const identifier = fd.get("identifier") as string;
                    const brand = fd.get("brand") as string;
                    const customerId = selectedCustomer?.id || "";

                    const isDuplicate = rackets.some(
                      (r) =>
                        r.customerId === customerId &&
                        r.name.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() ===
                          racketName.trim().toLowerCase() &&
                        (r.identifier || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() ===
                          (identifier || "").trim().toLowerCase() &&
                        (!racketFormDefault ||
                          r.id !== racketFormDefault.id ||
                          racketFormDefault.isClone),
                    );

                    if (isDuplicate) {
                      alert(
                        "Não é possível salvar: o cliente já possui uma raquete com este mesmo Nome e Identificador!",
                      );
                      return; // Prevent saving
                    }

                    const payload = {
                      brand,
                      name: racketName,
                      identifier,
                      stringPattern: fd.get("stringPattern") as string,
                      gripSize: fd.get("gripSize") as string,
                      sport: fd.get("sport") as string,
                      notes: fd.get("notes") as string,
                      weight: fd.get("weight") as string,
                      balance: fd.get("balance") as string,
                      length: fd.get("length") as string,
                      swingweight: fd.get("swingweight") as string,
                      spinweight: fd.get("spinweight") as string,
                      twistweight: fd.get("twistweight") as string,
                      recoilweight: fd.get("recoilweight") as string,
                      polarIndex: fd.get("polarIndex") as string,
                      stiffnessRA: fd.get("stiffnessRA") as string,
                      dynamicStiffnessHz: fd.get(
                        "dynamicStiffnessHz",
                      ) as string,
                      dynamicStiffnessDRA: fd.get(
                        "dynamicStiffnessDRA",
                      ) as string,
                    };

                    if (
                      racketFormDefault &&
                      racketFormDefault.id &&
                      !racketFormDefault.isClone
                    ) {
                      fetch(`${API_URL}/api/rackets/${racketFormDefault.id}`, {
                        method: "PUT",
                        headers: {
                          ...getAuthHeader(),
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                      })
                        .then(async (res) => {
                          if (!res.ok) {
                            const data = await res.json().catch(() => ({}));
                            alert(data.error || "Erro ao salvar a raquete.");
                          } else {
                            await fetchRackets();
                            setIsRacketModalOpen(false);
                          }
                        })
                        .catch(() => alert("Erro de rede ao salvar raquete."));
                    } else {
                      fetch(`${API_URL}/api/rackets`, {
                        method: "POST",
                        headers: {
                          ...getAuthHeader(),
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ ...payload, customerId }),
                      })
                        .then(async (res) => {
                          if (!res.ok) {
                            const data = await res.json().catch(() => ({}));
                            alert(data.error || "Erro ao criar a raquete.");
                          } else {
                            await fetchRackets();
                            setIsRacketModalOpen(false);
                          }
                        })
                        .catch(() => alert("Erro de rede ao criar raquete."));
                    }
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "20px",
                    }}
                  >
                    <div style={{ gridColumn: "span 2" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Raquete
                      </label>
                      <input
                        name="racketName"
                        required
                        style={inputStyle}
                        defaultValue={racketFormDefault?.name || ""}
                        list="racket-models-list"
                        placeholder="Selecione ou digite o modelo..."
                        autoComplete="off"
                      />
                      <datalist id="racket-models-list">
                        {[...(appSettings?.racketModels || [])]
                          .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                          .map((model: string) => (
                            <option key={model} value={model} />
                          ))}
                      </datalist>
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Marca da Raquete
                      </label>
                      <input
                        name="brand"
                        type="text"
                        placeholder="Ex: Wilson"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.brand || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Identificador
                      </label>
                      <input
                        name="identifier"
                        type="text"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.identifier || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Padrão de cordas
                      </label>
                      <input
                        name="stringPattern"
                        type="text"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.stringPattern || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Tamanho do Grip
                      </label>
                      <input
                        name="gripSize"
                        type="text"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.gripSize || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Esporte
                      </label>
                      <select
                        name="sport"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.sport || "Tênis"}
                      >
                        {[
                          ...(appSettings.sports || [
                            "Tênis",
                            "Beach Tennis",
                            "Squash",
                            "Badminton",
                            "Padel",
                          ]),
                        ]
                          .sort((a: any, b: any) => String(typeof a === 'string' ? a : a.name).localeCompare(String(typeof b === 'string' ? b : b.name), undefined, { sensitivity: 'base' }))
                          .map((s: string) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Observações
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      style={{ ...inputStyle, resize: "none" }}
                      defaultValue={racketFormDefault?.notes || ""}
                    ></textarea>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Peso (g)
                      </label>
                      <input
                        name="weight"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.weight || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Equilíbrio (mm)
                      </label>
                      <input
                        name="balance"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.balance || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Comprimento (mm)
                      </label>
                      <input
                        name="length"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.length || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Swingweight (kgcm²)
                      </label>
                      <input
                        name="swingweight"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.swingweight || ""}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Spinweight (kgcm²)
                      </label>
                      <input
                        name="spinweight"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.spinweight || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Twistweight (kgcm²)
                      </label>
                      <input
                        name="twistweight"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.twistweight || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Recoilweight (kgcm²)
                      </label>
                      <input
                        name="recoilweight"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.recoilweight || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Índice Polar
                      </label>
                      <input
                        name="polarIndex"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.polarIndex || ""}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Rigidez (RA)
                      </label>
                      <input
                        name="stiffnessRA"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={racketFormDefault?.stiffnessRA || ""}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Rigidez Dinâmica (Hz)
                      </label>
                      <input
                        name="dynamicStiffnessHz"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={
                          racketFormDefault?.dynamicStiffnessHz || ""
                        }
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Rigidez Dinâmica (DRA)
                      </label>
                      <input
                        name="dynamicStiffnessDRA"
                        type="number"
                        step="0.1"
                        style={inputStyle}
                        defaultValue={
                          racketFormDefault?.dynamicStiffnessDRA || ""
                        }
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "16px",
                      marginTop: "32px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsRacketModalOpen(false)}
                      style={{
                        padding: "16px 32px",
                        background: "transparent",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderRadius: "100px",
                        color: "white",
                        cursor: "pointer",
                        fontWeight: 700,
                      }}
                    >
                      Fechar
                    </button>
                    <button
                      type="submit"
                      className="button-primary"
                      style={{ padding: "16px 32px" }}
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Clone Racket Modal (Customer Rackets) */}
      <AnimatePresence>
        {isCloneRacketModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,12,60,0.8)",
              backdropFilter: "blur(8px)",
              zIndex: 100,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                width: "100%",
                maxWidth: "900px",
                maxHeight: "90vh",
                background: "var(--bg-panel-solid)",
                borderRadius: "32px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              }}
            >
              <div
                style={{
                  background: "#3A52EE",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    color: "white",
                    fontSize: "20px",
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  Raquetes do Cliente
                </h3>
                <button
                  onClick={() => setIsCloneRacketModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              <div
                style={{
                  padding: "32px",
                  overflowY: "auto",
                  flex: 1,
                  background: "rgba(255,255,255,0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <button
                    className="button-primary"
                    onClick={() => {
                      setIsCloneRacketModalOpen(false);
                      setRacketFormDefault(null);
                      setIsRacketModalOpen(true);
                    }}
                    style={{ padding: "12px 24px", fontSize: "14px" }}
                  >
                    <Plus size={16} /> Adicionar nova raquete
                  </button>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>
                      Buscar:
                    </span>
                    <input
                      type="text"
                      placeholder="Nome da raquete..."
                      style={{
                        padding: "10px 16px",
                        borderRadius: "8px",
                        border: "none",
                        background: "rgba(255,255,255,0.1)",
                        color: "white",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Table */}
                <div
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(200px, 3fr) 2fr 3fr 1fr",
                      padding: "16px 24px",
                      fontWeight: 600,
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      background: "rgba(255,255,255,0.05)",
                    }}
                  >
                    <div>Raquete</div>
                    <div>Último Encordoamento</div>
                    <div>Notas</div>
                    <div></div>
                  </div>

                  {(() => {
                    const custRackets = rackets.filter(
                      (r) => r.customerId === selectedCustomer?.id,
                    );
                    custRackets.sort((a, b) => {
                      const nameA = a.identifier
                        ? `${a.name.trim()} [${a.identifier}]`
                        : a.name.trim();
                      const nameB = b.identifier
                        ? `${b.name.trim()} [${b.identifier}]`
                        : b.name.trim();
                      return nameA.localeCompare(nameB, undefined, {
                        numeric: true,
                        sensitivity: "base",
                      });
                    });

                    if (custRackets.length === 0) {
                      return (
                        <div
                          style={{
                            padding: "32px",
                            textAlign: "center",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Nenhuma raquete cadastrada na base para este cliente.
                        </div>
                      );
                    }

                    return custRackets.map((racket) => {
                      const racketJobs = jobs
                        .filter((j) => j.racketId === racket.id)
                        .sort(
                          (a, b) =>
                            new Date(b.updatedAt).getTime() -
                            new Date(a.updatedAt).getTime(),
                        );
                      const lastJob = racketJobs[0];
                      return (
                        <div
                          key={racket.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(200px, 3fr) 2fr 3fr 1fr",
                            padding: "16px 24px",
                            alignItems: "center",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: "white" }}>
                              {racket.name}
                              {racket.identifier
                                ? ` [${racket.identifier}]`
                                : ""}
                            </div>
                          </div>
                          <div
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "14px",
                            }}
                          >
                            {lastJob
                              ? new Date(lastJob.updatedAt).toLocaleString(
                                  "pt-BR",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                              : "-"}
                          </div>
                          <div
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "14px",
                            }}
                          >
                            -
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: "8px",
                            }}
                          >
                            <button
                              onClick={() => {
                                setRacketFormDefault({
                                  ...racket,
                                  name: racket.name,
                                  identifier: "",
                                  isClone: true,
                                });
                                setIsCloneRacketModalOpen(false);
                                setIsRacketModalOpen(true);
                              }}
                              style={{
                                background: "rgba(255,255,255,0.1)",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Clonar raquete"
                            >
                              <Copy size={18} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedJobRacket(racket.id);
                                setIsCloneRacketModalOpen(false);
                              }}
                              style={{
                                background: "#4298E7",
                                border: "none",
                                padding: "8px",
                                borderRadius: "8px",
                                color: "white",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Selecionar raquete"
                            >
                              <ArrowRightCircle size={18} />
                            </button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "32px",
                  }}
                >
                  <button
                    onClick={() => setIsCloneRacketModalOpen(false)}
                    style={{
                      padding: "12px 32px",
                      background: "transparent",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderRadius: "100px",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {isProfessorModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,12,60,0.8)",
              backdropFilter: "blur(10px)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              style={{
                background: "var(--bg-panel-solid)",
                width: "100%",
                maxWidth: "500px",
                maxHeight: "90vh",
                borderRadius: "24px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                border: "1px solid var(--border-light)",
              }}
            >
              <form
                key={selectedProfessor?.id || "new"}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const fd = new FormData(e.currentTarget);
                  const firstName = fd.get("firstName") as string;
                  const lastName = fd.get("lastName") as string;
                  const emailValue = fd.get("email") as string;
                  const phoneValue = fd.get("phone") as string;

                  const newProf = {
                    name: `${firstName} ${lastName}`.trim(),
                    email:
                      emailValue && emailValue.trim() !== ""
                        ? emailValue
                        : "nao.informado@techtennis.com",
                    phone:
                      phoneValue && phoneValue.trim() !== ""
                        ? phoneValue
                        : "00000000000",
                    gender: fd.get("gender") as string,
                    birthDate: fd.get("birthDate") as string,
                    cpfCnpj: fd.get("cpfCnpj") as string,
                    address: fd.get("address") as string,
                    cep: fd.get("cep") as string,
                    city: fd.get("city") as string,
                    country: fd.get("country") as string,
                    yearsOfExperience: fd.get("yearsOfExperience") as string,
                    trainingTypes: fd.get("trainingTypes") as string,
                    numericId: fd.get("numericId")
                      ? parseInt(fd.get("numericId") as string, 10)
                      : undefined,
                  };
                  try {
                    let res;
                    if (selectedProfessor && selectedProfessor.id) {
                      res = await fetch(
                        `${API_URL}/api/professors/${selectedProfessor.id}`,
                        {
                          method: "PUT",
                          headers: {
                            ...getAuthHeader(),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify(newProf),
                        },
                      );
                    } else {
                      res = await fetch(`${API_URL}/api/professors`, {
                        method: "POST",
                        headers: {
                          ...getAuthHeader(),
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(newProf),
                      });
                    }
                    if (!res.ok) {
                      const errText = await res.text();
                      throw new Error(errText || "Erro da API");
                    }

                    const savedProfessor = await res.json();

                    if (import.meta.env.VITE_BFF_URL) {
                      try {
                        const method =
                          selectedProfessor && selectedProfessor.id
                            ? "PUT"
                            : "POST";
                        const url =
                          method === "PUT"
                            ? `${import.meta.env.VITE_BFF_URL}/api/clientes/${selectedProfessor.id}`
                            : `${import.meta.env.VITE_BFF_URL}/api/clientes`;

                        await fetch(url, {
                          method,
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            id: savedProfessor?.id,
                            numericId:
                              newProf.numericId || savedProfessor?.numericId,
                            name: newProf.name,
                            email: newProf.email,
                            phone: newProf.phone,
                            document: newProf.cpfCnpj || "",
                            address: newProf.address || "",
                            addressNumber:
                              (fd.get("addressNumber") as string) || "",
                            complement: (fd.get("complement") as string) || "",
                            neighborhood:
                              (fd.get("neighborhood") as string) || "",
                            city: newProf.city || "",
                            state: (fd.get("state") as string) || "",
                            zipCode: newProf.cep || "",
                          }),
                        });
                      } catch (bffErr) {
                        console.error("Erro ao sincronizar com BFF:", bffErr);
                      }
                    }

                    await fetchProfessors();
                    await fetchSyncStatuses();
                    setIsProfessorModalOpen(false);
                    setSelectedProfessor(null);
                  } catch (e: any) {
                    console.error("Error saving professor:", e);
                    alert(
                      "Falha ao salvar professor. Verifique os dados. (" +
                        e.message +
                        ")",
                    );
                  }
                }}
              >
                <div
                  style={{
                    padding: "24px",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "20px", color: "white" }}>
                    {selectedProfessor ? "Editar Professor" : "Novo Professor"}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setIsProfessorModalOpen(false)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                    }}
                  >
                    <X />
                  </button>
                </div>
                <div
                  style={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    overflowY: "auto",
                  }}
                >
                  {customerSyncStatusData &&
                    customerSyncStatusData.syncErrors && (
                      <div
                        style={{
                          marginBottom: "8px",
                          padding: "16px",
                          background: "rgba(255, 68, 68, 0.1)",
                          border: "1px solid #FF4444",
                          borderRadius: "8px",
                          color: "#FF4444",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <AlertCircle size={20} />
                          Erro de Sincronização com o Bling
                        </div>
                        <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                          {(() => {
                            try {
                              const parsed = JSON.parse(
                                customerSyncStatusData.syncErrors as string,
                              );
                              if (parsed.error && parsed.error.fields) {
                                return (
                                  <ul
                                    style={{ margin: 0, paddingLeft: "20px" }}
                                  >
                                    {parsed.error.fields.map(
                                      (f: any, idx: number) => (
                                        <li key={idx}>{f.msg}</li>
                                      ),
                                    )}
                                  </ul>
                                );
                              }
                              return (
                                parsed.error?.message ||
                                customerSyncStatusData.syncErrors
                              );
                            } catch {
                              return customerSyncStatusData.syncErrors;
                            }
                          })()}
                        </div>
                      </div>
                    )}
                  <FieldWrapper
                    appSettings={appSettings}
                    type="professorFields"
                    field="numericId"
                    label="ID TechTennis (Automático)"
                  >
                    {(status) => (
                      <input
                        name="numericId"
                        type="number"
                        disabled={status === "readonly"}
                        required={status === "mandatory"}
                        defaultValue={selectedProfessor?.numericId || ""}
                        placeholder="Ex: 104151 (ou deixe em branco para gerar novo)"
                        style={inputStyle}
                      />
                    )}
                  </FieldWrapper>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="firstName"
                      label="Nome"
                    >
                      {(status) => (
                        <input
                          name="firstName"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={
                            selectedProfessor
                              ? selectedProfessor.name?.split(" ")[0]
                              : ""
                          }
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="lastName"
                      label="Sobrenome"
                    >
                      {(status) => (
                        <input
                          name="lastName"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={
                            selectedProfessor
                              ? selectedProfessor.name
                                  ?.split(" ")
                                  .slice(1)
                                  .join(" ")
                              : ""
                          }
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="cpfCnpj"
                      label="CPF/CNPJ"
                    >
                      {(status) => (
                        <input
                          name="cpfCnpj"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.cpfCnpj || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="birthDate"
                      label="Data de Nascimento"
                    >
                      {(status) => (
                        <input
                          type="date"
                          name="birthDate"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={
                            selectedProfessor?.birthDate
                              ? selectedProfessor.birthDate.substring(0, 10)
                              : ""
                          }
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="gender"
                      label="Gênero"
                    >
                      {(status) => (
                        <select
                          name="gender"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.gender || ""}
                          style={inputStyle}
                        >
                          <option value="">Selecione...</option>
                          <option value="M">Masculino</option>
                          <option value="F">Feminino</option>
                          <option value="O">Outro</option>
                        </select>
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="phone"
                      label="Celular"
                    >
                      {(status) => (
                        <input
                          name="phone"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          onChange={(e) =>
                            (e.target.value = applyPhoneMask(e.target.value))
                          }
                          defaultValue={
                            selectedProfessor?.phone
                              ? applyPhoneMask(selectedProfessor.phone)
                              : ""
                          }
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="email"
                      label="Email"
                    >
                      {(status) => (
                        <input
                          type="email"
                          name="email"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.email || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="cep"
                      label="CEP"
                    >
                      {(status) => (
                        <input
                          name="cep"
                          onBlur={handleCepBlur}
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.cep || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="address"
                      label="Endereço"
                    >
                      {(status) => (
                        <input
                          name="address"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.address || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="addressNumber"
                      label="Número"
                    >
                      {(status) => (
                        <input
                          name="addressNumber"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.addressNumber || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="complement"
                      label="Complemento"
                    >
                      {(status) => (
                        <input
                          name="complement"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.complement || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="neighborhood"
                      label="Bairro"
                    >
                      {(status) => (
                        <input
                          name="neighborhood"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.neighborhood || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="city"
                      label="Cidade"
                    >
                      {(status) => (
                        <input
                          name="city"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.city || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="state"
                      label="Estado"
                    >
                      {(status) => (
                        <input
                          name="state"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.state || ""}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="country"
                      label="País"
                    >
                      {(status) => (
                        <input
                          name="country"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={selectedProfessor?.country || "Brasil"}
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                    <FieldWrapper
                      appSettings={appSettings}
                      type="professorFields"
                      field="yearsOfExperience"
                      label="Anos de Experiência"
                    >
                      {(status) => (
                        <input
                          type="number"
                          name="yearsOfExperience"
                          disabled={status === "readonly"}
                          required={status === "mandatory"}
                          defaultValue={
                            selectedProfessor?.yearsOfExperience || ""
                          }
                          style={inputStyle}
                        />
                      )}
                    </FieldWrapper>
                  </div>
                  <FieldWrapper
                    appSettings={appSettings}
                    type="professorFields"
                    field="trainingTypes"
                    label="Tipos de Treino (ex: Competitivo, Rebatedor)"
                  >
                    {(status) => (
                      <input
                        type="text"
                        name="trainingTypes"
                        disabled={status === "readonly"}
                        required={status === "mandatory"}
                        defaultValue={selectedProfessor?.trainingTypes || ""}
                        style={inputStyle}
                      />
                    )}
                  </FieldWrapper>
                </div>
                <div
                  style={{
                    padding: "24px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                  }}
                >
                  <button
                    type="submit"
                    className="button-primary"
                    style={{ padding: "12px 32px" }}
                  >
                    {selectedProfessor ? "Salvar Professor" : "Criar Professor"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {isPaymentModalOpen && activePaymentJob && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-panel"
              style={{
                width: "90%",
                maxWidth: "800px",
                background: "var(--bg-card)",
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  background: "var(--bg-panel)",
                }}
              >
                <h2 style={{ margin: 0, fontSize: "20px" }}>Preços</h2>
                <button
                  onClick={() => setIsPaymentModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--text-secondary)",
                    cursor: "pointer",
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ padding: "32px" }}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 2fr",
                      padding: "16px",
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                    }}
                  >
                    <div>Item</div>
                    <div>Preço</div>
                    <div>Notas</div>
                  </div>
                  {(() => {
                    let targetJobs = [activePaymentJob];
                    if (paymentMode === "order") {
                      const oCode =
                        activePaymentJob.orderCode ||
                        activePaymentJob.id.substring(0, 8).toUpperCase();
                      targetJobs = jobs.filter(
                        (j) =>
                          (j.orderCode ||
                            j.id.substring(0, 8).toUpperCase()) === oCode,
                      );
                    }

                    let totalBruto = 0;
                    let totalLiquido = 0;
                    const serviceLines: any[] = [];

                    targetJobs.forEach((tj) => {
                      const r = rackets?.find((r: any) => r.id === tj.racketId);
                      const rIdentifier = r?.identifier
                        ? ` [${r.identifier}]`
                        : "";
                      const rModel = tj.racketModel || "Raquete Customizada";
                      const racketDisplayName = `${rModel}${rIdentifier}`;

                      totalLiquido += tj.price || 0;

                      if (tj.auxServices && Array.isArray(tj.auxServices)) {
                        tj.auxServices
                          .filter((s: any) => s.isActive)
                          .forEach((s: any, idx: number) => {
                            const sPrice = s.price || 0;
                            totalBruto += sPrice;
                            serviceLines.push({
                              id: `${tj.id}-aux-${idx}`,
                              item: `${s.type} (${racketDisplayName})`,
                              price: sPrice,
                              notes: s.notes || "-",
                            });
                          });
                      }

                      if (
                        tj.isStringing ||
                        tj.type === "to_string" ||
                        tj.type === "stringing" ||
                        tj.type === "picking_up" ||
                        tj.type === "picked_up"
                      ) {
                        // Note: Many jobs might not have isStringing=true explicitly if they were created before that flag, but their type reflects it.
                        const base = tj.basePrice != null && tj.basePrice !== "" ? Number(tj.basePrice) : 120;
                        totalBruto += base;
                        serviceLines.push({
                          id: `${tj.id}-stringing`,
                          item: `Encordoamento (${racketDisplayName})`,
                          price: base,
                          notes: tj.racketNotes || "-",
                        });
                      }
                    });

                    const totalDesconto = totalBruto - totalLiquido;

                    return (
                      <>
                        {serviceLines.map((line) => (
                          <div
                            key={line.id}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "2fr 1fr 2fr",
                              padding: "16px",
                              borderBottom: "1px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <div>{line.item}</div>
                            <div>{line.price.toFixed(2)} BRL</div>
                            <div>{line.notes}</div>
                          </div>
                        ))}

                        {totalDesconto > 0.01 && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              padding: "16px 16px 8px 16px",
                              fontWeight: 500,
                              color: "#D93B65",
                            }}
                          >
                            <span style={{ marginRight: "16px" }}>
                              Desconto Total
                            </span>
                            <span>- {totalDesconto.toFixed(2)} BRL</span>
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "8px 16px 16px 16px",
                            fontWeight: 700,
                            fontSize: "18px",
                          }}
                        >
                          <span
                            style={{
                              marginRight: "16px",
                              color: "var(--text-secondary)",
                            }}
                          >
                            Total da Operação
                          </span>
                          <span>{totalLiquido.toFixed(2)} BRL</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div
                  style={{
                    marginTop: "24px",
                    fontSize: "14px",
                    fontWeight: 600,
                    minHeight: "24px",
                  }}
                >
                  {activePaymentJob.paid && (
                    <span>
                      Pagamento recebido em:{" "}
                      {new Date().toLocaleDateString("pt-BR")} 19:34
                    </span>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    marginTop: "32px",
                    paddingTop: "24px",
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {activePaymentJob.paid ? (
                    <button
                      onClick={async () => {
                        const previousJobs = [...jobs];
                        let targetJobs = [activePaymentJob];
                        if (paymentMode === "order") {
                          const oCode =
                            activePaymentJob.orderCode ||
                            activePaymentJob.id.substring(0, 8).toUpperCase();
                          targetJobs = jobs.filter(
                            (j) =>
                              (j.orderCode ||
                                j.id.substring(0, 8).toUpperCase()) === oCode,
                          );
                        }

                        await refundPrepaidDeduction(customers, targetJobs, API_URL, getAuthHeader);
                        await fetchCustomers();

                        setJobs(
                          jobs.map((j) =>
                            targetJobs.some((tj) => tj.id === j.id)
                              ? { ...j, paid: false, originalData: typeof j.originalData === 'string' ? JSON.stringify({ ...JSON.parse(j.originalData || '{}'), paymentMethod: null }) : { ...(j.originalData || {}), paymentMethod: null } }
                              : j,
                          ),
                        );
                        setIsPaymentModalOpen(false);
                        try {
                          await Promise.all(
                            targetJobs.map((tj) => {
                              const originalData = typeof tj.originalData === 'string' ? JSON.parse(tj.originalData || '{}') : (tj.originalData || {});
                              originalData.paymentMethod = null;
                              const newOriginalData = JSON.stringify(originalData);
                              return fetch(`${API_URL}/api/jobs/${tj.id}/status`, {
                                method: "PUT",
                                headers: {
                                  "Content-Type": "application/json",
                                  ...getAuthHeader(),
                                },
                                body: JSON.stringify({ paid: false, originalData: newOriginalData }),
                              });
                            }),
                          );
                        } catch (err) {
                          console.error(err);
                          alert("Erro ao atualizar o pagamento no servidor.");
                          setJobs(previousJobs);
                        }
                      }}
                      style={{
                        padding: "12px 24px",
                        background: "#E04A59",
                        border: "none",
                        borderRadius: "8px",
                        color: "white",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Cancelar Pagamento
                    </button>
                  ) : (
                    <>
                      {/* Inputs de Pagamento */}
                      {!activePaymentJob.paid && (
                        <div style={{ marginTop: '16px', marginBottom: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px' }}>
                          <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                            Código Verificador da Maquininha *
                          </label>
                          <input
                            type="text"
                            placeholder="Digite o código..."
                            value={paymentCode}
                            onChange={(e) => setPaymentCode(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              background: '#1e293b',
                              border: '1px solid #334155',
                              borderRadius: '8px',
                              color: 'white',
                              fontSize: '14px',
                            }}
                          />
                        </div>
                      )}
                      
                      <button
                        onClick={async () => {
                          if (!activePaymentJob.paid && (!paymentCode || paymentCode.trim() === '')) {
                            alert('Atenção: Por favor, informe o Código Verificador da Maquininha para confirmar o pagamento.');
                            return;
                          }
                          const previousJobs = [...jobs];
                          let targetJobs = [activePaymentJob];
                          if (paymentMode === "order") {
                            const oCode =
                              activePaymentJob.orderCode ||
                              activePaymentJob.id.substring(0, 8).toUpperCase();
                            targetJobs = jobs.filter(
                              (j) =>
                                (j.orderCode ||
                                  j.id.substring(0, 8).toUpperCase()) === oCode,
                            );
                          }

                          const { continueWithManualPayment, jobsToMarkPaid, aborted } = await handlePrepaidDeduction(customers, activePaymentJob.customerName, targetJobs, API_URL, getAuthHeader);

                          if (aborted) return;
                          await fetchCustomers();

                          let jobsToActuallyMarkPaid = jobsToMarkPaid;
                          if (continueWithManualPayment) {
                              jobsToActuallyMarkPaid = targetJobs;
                          }

                          const isPrepaid = !continueWithManualPayment && jobsToActuallyMarkPaid.length > 0;

                          setJobs(
                            jobs.map((j) =>
                              jobsToActuallyMarkPaid.some((tj) => tj.id === j.id)
                                ? { 
                                    ...j, 
                                    paid: true,
                                    paymentCode,
                                    originalData: isPrepaid ? (typeof j.originalData === 'string' ? JSON.stringify({ ...JSON.parse(j.originalData || '{}'), paymentMethod: 'PREPAID' }) : { ...(j.originalData || {}), paymentMethod: 'PREPAID' }) : j.originalData
                                  }
                                : j,
                            ),
                          );
                          setIsPaymentModalOpen(false);
                          try {
                            await Promise.all(
                              jobsToActuallyMarkPaid.map((tj) => {
                                const originalData = typeof tj.originalData === 'string' ? JSON.parse(tj.originalData || '{}') : (tj.originalData || {});
                                if (isPrepaid) originalData.paymentMethod = 'PREPAID';
                                
                                return fetch(`${API_URL}/api/jobs/${tj.id}/status`, {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                    ...getAuthHeader(),
                                  },
                                  body: JSON.stringify({ paid: true, paymentCode, originalData: isPrepaid ? JSON.stringify(originalData) : undefined }),
                                });
                              }),
                            );
                          } catch (err) {
                            console.error(err);
                            alert("Erro ao confirmar o pagamento no servidor.");
                            setJobs(previousJobs);
                          }
                        }}
                        style={{
                          padding: "12px 24px",
                          background: "#6FCF97",
                          border: "none",
                          borderRadius: "8px",
                          color: "var(--text-dark)",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Confirmar Pagamento
                      </button>
                    </>
                  )}
                  <button
                    style={{
                      padding: "12px 24px",
                      background: "#4298E7",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Editar Valores
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PICKUP CONFIRMATION MODAL */}
      <AnimatePresence>
        {isPickupModalOpen && activePickupJob && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-panel"
              style={{
                width: "90%",
                maxWidth: "600px",
                background: "var(--bg-panel)",
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <h2 style={{ margin: 0, fontSize: "20px" }}>
                  Confirmação de Retirada
                </h2>
                <button
                  onClick={() => {
                     setIsPickupModalOpen(false);
                     setPickupPaymentMode(null);
                     setPickupPaymentCode("");
                  }}
                  style={{
                    background: "white",
                    border: "none",
                    color: "var(--text-dark)",
                    cursor: "pointer",
                    borderRadius: "8px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                  }}
                >
                  X
                </button>
              </div>

              <div
                style={{
                  padding: "32px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {pickupPaymentMode ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <label style={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>Código Verificador da Maquininha *</label>
                    <input
                      type="text"
                      placeholder="Digite o código..."
                      value={pickupPaymentCode}
                      onChange={(e) => setPickupPaymentCode(e.target.value)}
                      style={{
                        padding: "14px",
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.1)",
                        border: "1px solid var(--border-light)",
                        color: "white",
                        width: "100%",
                      }}
                    />
                    <button
                      onClick={async () => {
                        if (!pickupPaymentCode || pickupPaymentCode.trim() === "") {
                          alert("Atenção: Por favor, informe o Código Verificador da Maquininha para confirmar o pagamento.");
                          return;
                        }

                        if (pickupPaymentMode === "single") {
                            const previousJobs = [...jobs];
                            const targetJobs = [activePickupJob];

                            const { continueWithManualPayment, jobsToMarkPaid, aborted } = await handlePrepaidDeduction(customers, activePickupJob.customerName, targetJobs, API_URL, getAuthHeader);

                            if (aborted) return;
                            await fetchCustomers();

                            let jobsToActuallyMarkPaid = jobsToMarkPaid;
                            if (continueWithManualPayment) {
                                jobsToActuallyMarkPaid = targetJobs;
                            }

                            setJobs((prev) =>
                              prev.map((j) => {
                                 if (j.id === activePickupJob.id) {
                                    const isPaid = jobsToActuallyMarkPaid.some(tj => tj.id === j.id);
                                    return {
                                       ...j,
                                       type: "picked_up",
                                       status: "entregue",
                                       paid: isPaid ? true : j.paid,
                                       paymentCode: isPaid ? pickupPaymentCode : undefined
                                     }
                                 }
                                 return j;
                              }),
                            );
                            setIsPickupModalOpen(false);
                            setPickupPaymentMode(null);
                            setPickupPaymentCode("");
                            try {
                              const isPaid = jobsToActuallyMarkPaid.some(p => p.id === activePickupJob.id);
                              await fetch(
                                `${API_URL}/api/jobs/${activePickupJob.id}/status`,
                                {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                    ...getAuthHeader(),
                                  },
                                  body: JSON.stringify({
                                    type: "picked_up",
                                    status: "entregue",
                                    paid: isPaid ? true : activePickupJob.paid,
                                    paymentCode: isPaid ? pickupPaymentCode : undefined
                                  }),
                                },
                              );
                            } catch (err) {
                              console.error(err);
                              setJobs(previousJobs);
                            }
                        } else {
                            const previousJobs = [...jobs];
                            const targetJobs = jobs.filter(
                              (j) =>
                                j.customerName === activePickupJob.customerName &&
                                j.type === "picking_up",
                            );
                            
                            const { continueWithManualPayment, jobsToMarkPaid, aborted } = await handlePrepaidDeduction(customers, activePickupJob.customerName, targetJobs, API_URL, getAuthHeader);

                            if (aborted) return;

                            let jobsToActuallyMarkPaid = jobsToMarkPaid;
                            if (continueWithManualPayment) {
                                jobsToActuallyMarkPaid = targetJobs;
                            }
                            
                            setJobs((prev) =>
                              prev.map((j) => {
                                const isTarget = j.customerName === activePickupJob.customerName && j.type === "picking_up";
                                const isPaid = jobsToActuallyMarkPaid.some(tj => tj.id === j.id);
                                if (isTarget) {
                                   return {
                                      ...j,
                                      type: "picked_up",
                                      status: "entregue",
                                      paid: isPaid ? true : j.paid,
                                      paymentCode: isPaid ? pickupPaymentCode : undefined
                                   }
                                }
                                return j;
                              }),
                            );
                            setIsPickupModalOpen(false);
                            setPickupPaymentMode(null);
                            setPickupPaymentCode("");
                            try {
                              await Promise.all(
                                targetJobs.map((tj) => {
                                  const isPaid = jobsToActuallyMarkPaid.some(p => p.id === tj.id);
                                  return fetch(`${API_URL}/api/jobs/${tj.id}/status`, {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                      ...getAuthHeader(),
                                    },
                                    body: JSON.stringify({
                                      type: "picked_up",
                                      status: "entregue",
                                      paid: isPaid ? true : tj.paid,
                                      paymentCode: isPaid ? pickupPaymentCode : undefined
                                    }),
                                  });
                                }),
                              );
                            } catch (err) {
                              console.error(err);
                              setJobs(previousJobs);
                            }
                        }
                      }}
                      style={{
                        background: "#6FCF97",
                        border: "none",
                        borderRadius: "8px",
                        padding: "16px",
                        color: "var(--text-dark)",
                        fontWeight: 600,
                        fontSize: "16px",
                        cursor: "pointer",
                      }}
                    >
                      Confirmar Retirada
                    </button>
                    <button
                      onClick={() => setPickupPaymentMode(null)}
                      style={{
                        background: "transparent",
                        border: "1px solid white",
                        borderRadius: "8px",
                        padding: "12px",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      Voltar
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        if (activePickupJob && !activePickupJob.paid) {
                          setPickupPaymentMode("single");
                        } else {
                          // Se já estiver pago, executar diretamente (replicar a lógica original)
                          // Não é necessário pois se já está pago, não exibe os botões "Não paga", mas para evitar duplicar código aqui,
                          // apenas alteramos para sempre pedir o código. 
                          // Wait, if it is already paid, the user shouldn't be asked for a payment code. But the buttons for "(Paga)" imply you want to pay.
                          // Se o usuário clicar em "Paga" num já pago (não devia ser possível ou os botões não aparecem). Na verdade aparecem sim se já estiver pago, significando "entregar (já pago)".
                          // Mas vamos pedir o modo de qualquer forma se não estiver pago. Se estiver pago, o usuário não clica aqui, ele clica na ordem já paga. 
                          // Vamos forçar que abre o modo single e pede o código (pois se clicar "Paga", é para pagar).
                          setPickupPaymentMode("single");
                        }
                      }}
                      style={{
                        background: "#F2C94C",
                        border: "none",
                        borderRadius: "8px",
                        padding: "24px",
                        color: "var(--text-dark)",
                        fontWeight: 600,
                        fontSize: "16px",
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ marginBottom: "4px" }}>Raquete única</div>
                      <div>(Paga)</div>
                    </button>

                    <button
                      onClick={() => {
                         setPickupPaymentMode("order");
                      }}
                      style={{
                        background: "#D93B65",
                        border: "none",
                        borderRadius: "8px",
                        padding: "24px",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "16px",
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ marginBottom: "4px" }}>Ordem completa</div>
                      <div>(Paga)</div>
                    </button>

                    {!activePickupJob.paid && (
                      <>
                        <button
                          onClick={async () => {
                            const previousJobs = [...jobs];
                            setJobs((prev) =>
                              prev.map((j) =>
                                j.id === activePickupJob.id
                                  ? { ...j, type: "picked_up", status: "entregue" }
                                  : j,
                              ),
                            );
                            setIsPickupModalOpen(false);
                            try {
                              await fetch(
                                `${API_URL}/api/jobs/${activePickupJob.id}/status`,
                                {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                    ...getAuthHeader(),
                                  },
                                  body: JSON.stringify({
                                    type: "picked_up",
                                    status: "entregue",
                                  }),
                                },
                              );
                            } catch (err) {
                              console.error(err);
                              setJobs(previousJobs);
                            }
                          }}
                          style={{
                            background: "#FFF9E6",
                            border: "none",
                            borderRadius: "8px",
                            padding: "24px",
                            color: "var(--text-dark)",
                            fontWeight: 600,
                            fontSize: "16px",
                            cursor: "pointer",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ marginBottom: "4px" }}>Raquete única</div>
                          <div>(Não paga)</div>
                        </button>

                        <button
                          onClick={async () => {
                            const previousJobs = [...jobs];
                            const targetJobs = jobs.filter(
                              (j) =>
                                j.customerName === activePickupJob.customerName &&
                                j.type === "picking_up",
                            );
                            setJobs((prev) =>
                              prev.map((j) =>
                                j.customerName === activePickupJob.customerName &&
                                j.type === "picking_up"
                                  ? { ...j, type: "picked_up", status: "entregue" }
                                  : j,
                              ),
                            );
                            setIsPickupModalOpen(false);
                            try {
                              await Promise.all(
                                targetJobs.map((tj) =>
                                  fetch(`${API_URL}/api/jobs/${tj.id}/status`, {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                      ...getAuthHeader(),
                                    },
                                    body: JSON.stringify({
                                      type: "picked_up",
                                      status: "entregue",
                                    }),
                                  }),
                                ),
                              );
                            } catch (err) {
                              console.error(err);
                              setJobs(previousJobs);
                            }
                          }}
                          style={{
                            background: "#FFF0F5",
                            border: "none",
                            borderRadius: "8px",
                            padding: "24px",
                            color: "#D93B65",
                            fontWeight: 600,
                            fontSize: "16px",
                            cursor: "pointer",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ marginBottom: "4px" }}>Ordem completa</div>
                          <div>(Não paga)</div>
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFeedbackModalOpen && activeFeedbackJob && (
          <FeedbackModal
            isOpen={isFeedbackModalOpen}
            onClose={() => {
              setIsFeedbackModalOpen(false);
              setActiveFeedbackJob(null);
            }}
            job={activeFeedbackJob}
            onSaveFeedback={async (newFeedback: any) => {
              try {
                const updatedJob = {
                  ...activeFeedbackJob,
                  feedback: newFeedback,
                };
                const res = await fetch(
                  `${API_URL}/api/jobs/${activeFeedbackJob.id}`,
                  {
                    method: "PUT",
                    headers: {
                      ...getAuthHeader(),
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updatedJob),
                  },
                );
                if (!res.ok)
                  throw new Error("Erro ao salvar resposta de feedback");
                setIsFeedbackModalOpen(false);
                setActiveFeedbackJob(null);
                await fetchJobs();
              } catch (err: any) {
                console.error(err);
                alert("Erro ao salvar: " + err.message);
              }
            }}
            readOnly={true}
          />
        )}
      </AnimatePresence>

      {/* LGPD Acceptance Modal */}
      <AnimatePresence>
        {showLgpdModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.85)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{
                background: "var(--bg-panel-solid)",
                padding: "32px",
                borderRadius: "16px",
                maxWidth: "500px",
                width: "100%",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <h2
                style={{
                  color: "white",
                  marginTop: 0,
                  marginBottom: "16px",
                  fontSize: "24px",
                }}
              >
                Aviso de Privacidade 🛡️
              </h2>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "15px",
                  lineHeight: "1.6",
                  marginBottom: "24px",
                }}
              >
                Para continuarmos oferecendo a melhor experiência na
                TechTennisBR e estarmos em conformidade com a{" "}
                <strong>Lei Geral de Proteção de Dados (LGPD)</strong>,
                precisamos que você leia e aceite nossos termos.
              </p>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  marginBottom: "32px",
                }}
              >
                Garantimos que seus dados são usados exclusivamente para a
                prestação dos nossos serviços de encordoamento e gestão de
                clientes, não sendo compartilhados com terceiros não
                autorizados. Ao clicar em "Li e Aceito", você concorda com
                nossos{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setLegalModalTab("terms");
                    setLegalModalOpen(true);
                  }}
                  style={{ color: "var(--primary-color)" }}
                >
                  Termos de Uso
                </a>{" "}
                e{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setLegalModalTab("privacy");
                    setLegalModalOpen(true);
                  }}
                  style={{ color: "var(--primary-color)" }}
                >
                  Política de Privacidade
                </a>
                .
              </p>

              <div style={{ display: "flex", gap: "16px" }}>
                <button
                  onClick={handleDeclineLgpd}
                  style={{
                    flex: 1,
                    padding: "14px",
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.2)",
                    color: "white",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Recusar (Sair)
                </button>
                <button
                  onClick={handleAcceptLgpd}
                  disabled={isAcceptingLgpd}
                  style={{
                    flex: 2,
                    padding: "14px",
                    background: "var(--primary-color)",
                    border: "none",
                    color: 'white',
                    borderRadius: "8px",
                    cursor: isAcceptingLgpd ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    opacity: isAcceptingLgpd ? 0.7 : 1,
                  }}
                >
                  {isAcceptingLgpd ? "Salvando..." : "Li e Aceito"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <LegalDocsModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        defaultTab={legalModalTab}
      />
    </div>
  );
};
